// This file is auto-generated by @hey-api/openapi-ts

import * as z from 'zod';

/**
 * AdapterSource
 */
export const zAdapterSource = z.enum([
    'pam_backend',
    'psp',
    'game_aggregator',
    'crm',
    'affiliate_platform'
]);

/**
 * AffiliateClaimItem
 */
export const zAffiliateClaimItem = z.object({
    affiliate_id: z.string(),
    affiliate_label: z.string().nullable(),
    claim_month: z.string(),
    claimed_deposit_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    claimed_ftd_count: z.int(),
    claimed_registrations: z.int().nullable(),
    deal: z.string().nullable(),
    media_tracker: z.string().nullable(),
    reported_net_gaming_revenue: z.string().regex(/^-?\d+\.\d{2,8}$/).nullable(),
    reported_total_commission: z.string().regex(/^-?\d+\.\d{2,8}$/).nullable(),
    source_platform: z.string(),
    source_ref: z.string().nullable()
});

/**
 * AffiliateClaimsListResponse
 */
export const zAffiliateClaimsListResponse = z.object({
    claims: z.array(zAffiliateClaimItem),
    limit: z.int(),
    offset: z.int()
});

/**
 * AffiliateQualityHeadline
 *
 * The affiliate's leading open case, carried beside its quality numbers.
 *
 * Issue #245. The digest showed three numbers per affiliate and nothing about
 * what BetQ had concluded from them, so a reader had to know the #139 decision
 * to make sense of a high score beside a loss. The conclusion already exists —
 * it is the affiliate's active recommendation — and the digest simply never
 * rendered it.
 *
 * Nothing here is computed. Every field is copied from one stored row named by
 * `recommendation_id`, chosen by the same `headline_case` the grouped inbox
 * uses, so the two screens cannot name different leading cases for one
 * affiliate.
 *
 * Nested and nullable rather than six flat nullable fields on the mover: an
 * affiliate with no open case is one absent object, not five nulls and a zero
 * that a client has to reassemble into that meaning.
 */
export const zAffiliateQualityHeadline = z.object({
    other_case_count: z.int(),
    other_case_outcome: z.string().nullable(),
    outcome: z.string().nullable(),
    pattern_id: z.string(),
    pending_review: z.boolean(),
    recommendation_id: z.uuid()
});

/**
 * AffiliateQualityMover
 *
 * One row of the digest's affiliate quality list.
 *
 * Typed rather than left as an opaque dict (#139). `list[dict[str, Any]]`
 * generates as `Array<{[key: string]: unknown}>` and
 * `z.record(z.string(), z.unknown())`, so nothing on either side of the wire
 * knew what these rows contain -- which is the shape issue #100 is the worked
 * example of: the API sent a string map, the panel cast it, and lint, `tsc`
 * and every test passed while the screen misreported a compliance fact.
 *
 * Money and ratios are decimal strings, never JSON floats.
 */
export const zAffiliateQualityMover = z.object({
    affiliate_id: z.string(),
    affiliate_ltv: z.string(),
    headline: zAffiliateQualityHeadline.nullish(),
    ltv_cpa_ratio: z.string(),
    metric_date: z.string(),
    quality_score: z.string(),
    sample_guard_passed: z.boolean()
});

/**
 * AffiliateQualityOrder
 *
 * The direction of `AffiliateQualitySort`, as the issue's URL asks for it
 * (`?sort=&order=asc|desc`). Split from the column rather than folded into it
 * (`EntityOrder`'s `alpha`/`alpha_desc` shape) because this table sorts five
 * columns both ways, and a combined enum would need ten values naming the
 * same two directions five times.
 */
export const zAffiliateQualityOrder = z.enum(['asc', 'desc']);

/**
 * AffiliateQualityRow
 */
export const zAffiliateQualityRow = z.object({
    affiliate_id: z.string(),
    computed_at: z.iso.datetime({ offset: true }),
    drivers: z.record(z.string(), z.unknown()),
    ltv_cpa_ratio: z.string(),
    metric_date: z.iso.date(),
    quality_score: z.string(),
    sample_guard_passed: z.boolean()
});

/**
 * AffiliateQualityResponse
 */
export const zAffiliateQualityResponse = z.object({
    affiliate_id: z.string(),
    history: z.array(zAffiliateQualityRow)
});

/**
 * AffiliateQualitySort
 *
 * Which column the All affiliates table is ordered by (#797).
 *
 * A closed set rather than a free string, the #466/#491 shape: an unknown
 * value is a 422, never a silent fall-back to the default. A typo that
 * quietly returns the default ordering is the worse failure -- the operator
 * reads a correct-looking page that answers a different question from the one
 * they asked.
 *
 * FIVE values, not six. The table displays the leading active case and
 * deliberately does NOT sort on it: founder decision (Lena) 2026-09-06 on
 * issue #797 Q2, verbatim **"2. d"** -- drop leading-case sorting from the
 * acceptance criteria. There is no between-affiliate comparator for a leading
 * case to begin with: `headline_case` is a WITHIN-affiliate selector applied
 * after the page's LIMIT (`recommendations/precedence.py`), and
 * re-implementing it in SQL is barred by the contract rule that one affiliate
 * must not have two different leading cases on two screens.
 */
export const zAffiliateQualitySort = z.enum([
    'metric_date',
    'affiliate',
    'quality_score',
    'ltv_cpa_ratio',
    'affiliate_ltv'
]);

/**
 * AffiliateQualityTableRow
 *
 * One row of the All affiliates table (#797).
 *
 * The digest mover's fields, with ONE difference that is the whole of the
 * issue's blocking question: the affiliate is named by its pseudonymous ref
 * rather than by the raw operator key. Founder decision (Lena) 2026-09-06 on
 * #797 Q1, verbatim **"1. b"** -- the affiliate column carries the
 * pseudonymous ref, as every other list surface does.
 *
 * A separate model rather than `AffiliateQualityMover` with a renamed field,
 * so the type system carries the rule: a raw affiliate id has nowhere to go
 * on this response. The pre-existing digest movers list keeps `affiliate_id`
 * unchanged -- #245's recorded posture, and open backlog item 41 is answered
 * for THIS surface only.
 *
 * Money and ratios are decimal strings, never JSON floats.
 */
export const zAffiliateQualityTableRow = z.object({
    affiliate_ltv: z.string(),
    affiliate_ref: z.string(),
    headline: zAffiliateQualityHeadline.nullish(),
    ltv_cpa_ratio: z.string(),
    metric_date: z.string(),
    quality_score: z.string(),
    sample_guard_passed: z.boolean()
});

/**
 * AffiliateQualityTableResponse
 *
 * Every scored affiliate of the tenant, one row each, paginated (#797).
 *
 * `total` is the count of AFFILIATES behind the page, not of score rows: the
 * table is one row per affiliate (the newest score, `DISTINCT ON`, as the
 * panel's five are chosen), so that is what the pager divides.
 *
 * `sort` and `order` are echoed back because the view is meant to be
 * shareable through the URL. The server states the ordering it actually
 * applied, so a client never has to infer it from the rows.
 *
 * NOTHING HERE IS A RANK. The default is recency and no field is called a
 * rank, top, best or a mover: BetQ ranks no affiliate, and the operator's
 * chosen ordering is the operator's (founder decision 2026-08-11, restated as
 * the issue's own first constraint).
 */
export const zAffiliateQualityTableResponse = z.object({
    limit: z.int(),
    offset: z.int(),
    order: zAffiliateQualityOrder,
    rows: z.array(zAffiliateQualityTableRow),
    sort: zAffiliateQualitySort,
    total: z.int()
});

/**
 * AttributionDivergence
 *
 * How often the signup tag and the current attribution name different affiliates.
 *
 * Issue #408. Since the #207 decision BetQ deliberately holds two affiliate
 * answers for one player: the registration event's tag, a traffic fact fixed
 * at signup, and the operator's attribution feed, a commercial fact restated
 * month by month. Divergence between them is ordinary business -- a
 * re-attribution, a fraud reversal, a de-tag -- and each metric family stays
 * internally honest either way. What was missing is that the divergence was
 * counted nowhere, so a *rising* one (tag hijacking, or a feed going wrong)
 * looked exactly like a steady one.
 *
 * Informational. Nothing reads these numbers back into a metric, a pattern or
 * a verdict.
 *
 * `comparable` is the denominator and is why `divergent` can be read at all:
 * 12 is a different fact against 15 players than against 40000. It is the
 * population where BOTH sides state an affiliate by name, which is exactly the
 * population the comparison is defined over.
 *
 * Absent is not different (the same three-state discipline as attribution
 * itself, `PlayerAffiliateAttribution`), so neither number counts:
 *
 * - a player with no attribution row at all -- the operator has said nothing,
 * and silence is not a contradicting statement;
 * - a player the operator has deliberately de-tagged -- an empty tag in the
 * feed is a STATEMENT that the player belongs to nobody, not a claim that
 * they belong to somebody else;
 * - a player whose registration event carries no tag -- organic traffic has no
 * signup tag to differ from.
 *
 * "Current" is the statement in force today, resolved exactly as
 * `resolve_player_affiliate` resolves it for a metric date: the latest
 * statement on or before the Europe/Berlin month in progress, carried forward
 * over months the operator did not restate. A statement dated in a future
 * month is therefore not yet in force and does not create a divergence.
 */
export const zAttributionDivergence = z.object({
    comparable: z.int(),
    divergent: z.int()
});

/**
 * AttributionRowRejection
 */
export const zAttributionRowRejection = z.object({
    code: z.string(),
    reason: z.string(),
    row_number: z.int()
});

/**
 * AttributionUploadMode
 *
 * How an upload combines with what is already stored.
 *
 * Explicit rather than inferred: a partial file uploaded as a `snapshot` drops
 * every player it omits, which is correct for a full monthly export and
 * destructive for a correction.
 *
 * Either mode can express a de-tag, because a de-tag is a row with an empty
 * tag rather than an omission. Mind the difference in blast radius: in a
 * snapshot an omitted player merely has nothing stated for that month (so the
 * carry-forward rule applies to them), while in a merge an omitted player is
 * untouched. Neither mode can delete a stored row — "no statement" is not
 * something an upload can restore once a statement exists.
 */
export const zAttributionUploadMode = z.enum(['snapshot', 'merge']);

/**
 * AuthSessionCreateRequest
 */
export const zAuthSessionCreateRequest = z.object({
    email: z.string().min(3).max(255),
    password: z.string().max(255).nullish(),
    tenant_slug: z.string().min(1).max(63).nullish()
});

/**
 * AuthTenantCandidate
 */
export const zAuthTenantCandidate = z.object({
    operator_name: z.string(),
    tenant_slug: z.string()
});

/**
 * AuthTenantResolutionResponse
 *
 * The bare `app.*`/`api.*` host's answer once a submitted credential
 * verifies against one or more operators (issue #70, founder decision
 * 2026-08-10). Never a session: "one match routes to the tenant host" is
 * the founder's own phrasing, so even a single verified match hands back a
 * candidate rather than minting a session on the tenantless host. The
 * caller retries `POST /v1/auth/session` with `tenant_slug` set to the
 * chosen candidate's slug -- the existing, unchanged tenant-host login
 * path -- or a browser client navigates to that tenant's host directly.
 */
export const zAuthTenantResolutionResponse = z.object({
    candidates: z.array(zAuthTenantCandidate)
});

/**
 * BackdatedRestatement
 *
 * Months in the upload that already have computed metrics.
 *
 * A backdated correction is honoured and propagates (2026-08-01 decision), and
 * it happens perhaps twice in a decade — which is exactly why it has to
 * announce itself. Silently, the sequence is: an upload that looks like any
 * other, numbers that move on the computation that upload itself queued
 * (#375 — within minutes, with no confirm step to catch it), and revenue
 * shifting between affiliates across closed months, which reads as corruption
 * because nothing distinguishes it from one. This response is the only
 * announcement there is.
 *
 * `months` are the restated months whose metrics are already computed, and
 * `player_count` is how many players the upload states for them. Empty months
 * means this upload changes nothing already computed.
 */
export const zBackdatedRestatement = z.object({
    months: z.array(z.string()),
    player_count: z.int()
});

/**
 * Body_preview_csv_v1_csv_preview_post
 */
export const zBodyPreviewCsvV1CsvPreviewPost = z.object({
    file: z.string(),
    mapping_contract: z.string().nullish(),
    source: z.string().nullish()
});

/**
 * Body_upload_affiliate_claims_v1_affiliates_claims_upload_post
 */
export const zBodyUploadAffiliateClaimsV1AffiliatesClaimsUploadPost = z.object({
    file: z.string(),
    source_platform: z.string().optional().default('income_access')
});

/**
 * Body_upload_csv_v1_csv_upload_post
 */
export const zBodyUploadCsvV1CsvUploadPost = z.object({
    file: z.string(),
    mapping_contract: z.string().nullish(),
    source: z.string().nullish()
});

/**
 * Body_upload_player_attribution_v1_affiliates_attribution_upload_post
 */
export const zBodyUploadPlayerAttributionV1AffiliatesAttributionUploadPost = z.object({
    file: z.string(),
    mode: zAttributionUploadMode.optional().default('snapshot'),
    source_platform: z.string().optional().default('pam_backend')
});

/**
 * BulkActionOutcome
 *
 * How many rows ended in this status.
 *
 * Not decoration, and not the same thing as `applied`. A bulk approve does
 * **not** uniformly produce `open`: `status_after_compliance_approval` sends an
 * `rg_only` cell to `rg_only` and a `blocked` one to `suppressed`, and a cell
 * the matrix does not list at all is `blocked` too. One approve can therefore
 * scatter a selection across three statuses, two of which leave the queue --
 * and since the row's expanded panel leaves with the row, this is the only
 * place the officer learns what happened.
 */
export const zBulkActionOutcome = z.object({
    count: z.int(),
    status: z.string()
});

/**
 * BulkComplianceVerdictRequest
 */
export const zBulkComplianceVerdictRequest = z.object({
    action_type: z.string().min(1).max(128),
    reason: z.string().min(1).max(2000),
    recommendation_ids: z.array(z.uuid()).min(1).max(100),
    verdict: z.enum(['approve', 'reject'])
});

/**
 * BulkRecommendationActionRequest
 */
export const zBulkRecommendationActionRequest = z.object({
    action: z.enum(['ack', 'dismiss']),
    reason: z.string().max(2000).nullish(),
    recommendation_ids: z.array(z.uuid()).min(1).max(100)
});

/**
 * BulkSkipCode
 *
 * Why one id in a batch was not acted on.
 *
 * Machine codes, never prose: the UI keys DE+EN off them
 * (AGENTS.md section 9.1, failure shape 2 -- an API `detail` rendered as UI copy
 * is untranslated English). Each is the batch-shaped form of a per-row response
 * the single-id endpoints already give:
 *
 * * `not_found` -- 404 from `get_recommendation`; an id outside the tenant is
 * indistinguishable from one that does not exist, which is the point.
 * * `superseded` -- 409, the row was folded into a case row while the page was
 * open.
 * * `wrong_status` -- 409, the state machine refuses the transition. Routine on
 * the inbox, where the `actionable` tab holds `open` **and** `acked` rows, so
 * a select-all-then-ack legitimately contains rows `ack` cannot touch.
 * * `action_type_mismatch` -- compliance only. A bulk verdict is scoped to one
 * `action_type` group (founder decision, 2026-08-09); this enforces the scope
 * server-side instead of trusting the screen to have grouped correctly.
 */
export const zBulkSkipCode = z.enum([
    'not_found',
    'superseded',
    'wrong_status',
    'action_type_mismatch'
]);

/**
 * BulkActionSkip
 */
export const zBulkActionSkip = z.object({
    code: zBulkSkipCode,
    recommendation_id: z.uuid()
});

/**
 * BulkActionResponse
 */
export const zBulkActionResponse = z.object({
    applied: z.int(),
    outcomes: z.array(zBulkActionOutcome),
    requested: z.int(),
    skipped: z.array(zBulkActionSkip)
});

/**
 * CareRecordRequest
 *
 * What a person did about a duty-of-care case, in their own words (#434).
 *
 * A free-text note and nothing else. The founder decision of 2026-08-11 is
 * explicit that BetQ does not own the operator's responsible-gambling
 * procedure and therefore does not invent its taxonomy -- an option list here
 * would be a claim about their process. A structured category set is post-MVP
 * and operator-configurable, if ever.
 *
 * Required, and required in the strong sense: `min_length=1` alone would let
 * `"   "` through, and a case closed with a blank note is a case closed with
 * no record of what was done -- which is the one thing this endpoint exists
 * to prevent. The validator strips before the check and STORES the stripped
 * value, so the trail never carries an accidental leading newline from a
 * textarea either.
 *
 * Deliberately not `RecommendationActionRequest` with `note` made required:
 * that model's two optional fields are the ack/dismiss/execute shape, and a
 * caller sending `{"reason": "..."}` here would be accepted by the schema and
 * then close a care case with an empty record.
 */
export const zCareRecordRequest = z.object({
    note: z.string().min(1).max(2000)
});

/**
 * ClaimRowRejection
 */
export const zClaimRowRejection = z.object({
    code: z.string(),
    reason: z.string(),
    row_number: z.int()
});

/**
 * ClaimsMappingStatus
 *
 * How one affiliate platform's claims mapping stands (issue #379).
 *
 * Its own set rather than a reuse of `CsvMappingStatus`, because of the
 * fourth member. A CSV row on the console exists BECAUSE tenant_config
 * declares it, so `undeclared` is unreachable there and putting it in that
 * enum would publish a value the CSV path can never produce. On the claims
 * side the list is swept over declared platforms UNION platforms that already
 * have stored claims, so a platform ingesting under the built-in Income
 * Access dialects is a real and supported row -- and calling it `ready` would
 * claim a contract was loaded when none was declared.
 */
export const zClaimsMappingStatus = z.enum([
    'ready',
    'missing',
    'invalid',
    'undeclared'
]);

/**
 * ComplianceMatrixApprovalRequest
 */
export const zComplianceMatrixApprovalRequest = z.object({
    approval_ref: z.string().min(1).max(255),
    jurisdiction: z.string().min(1),
    notes: z.string().max(2000).nullish()
});

/**
 * ComplianceMatrixClassPatchRequest
 *
 * Reclassify one matrix row for this tenant (#632).
 *
 * Separate from `ComplianceMatrixPatchRequest` rather than an optional field
 * on it: a cell edit and a reclassification are different acts with different
 * refusals, and one request body carrying either would make "which one did the
 * operator mean" a runtime question. The `reason` is required for the same
 * reason it is on a cell edit -- it is stored in the append-only changelog and
 * is the whole record of why the compliant default was left.
 */
export const zComplianceMatrixClassPatchRequest = z.object({
    jurisdiction: z.string().min(1),
    jurisdiction_class: z.enum(['regulated', 'unregulated']),
    reason: z.string().min(1).max(2000)
});

/**
 * ComplianceMatrixPatchRequest
 */
export const zComplianceMatrixPatchRequest = z.object({
    action_type: z.string().min(1),
    jurisdiction: z.string().min(1),
    reason: z.string().min(1).max(2000),
    value: z.enum([
        'allowed',
        'blocked',
        'rg_only',
        'requires_review'
    ])
});

/**
 * ComplianceReopenRequest
 *
 * Reason is mandatory, as on every other compliance act (issue #76, Q3).
 */
export const zComplianceReopenRequest = z.object({
    reason: z.string().min(1).max(2000)
});

/**
 * ComplianceVerdictRequest
 */
export const zComplianceVerdictRequest = z.object({
    reason: z.string().min(1).max(2000),
    verdict: z.enum(['approve', 'reject'])
});

/**
 * ComputationRequestStatus
 *
 * The lifecycle of an upload's "please compute this" note (issue #204).
 *
 * Deliberately three quarters the same vocabulary as `ComputationRunStatus`,
 * with `queued` for the state a run cannot be in. All four already have DE+EN
 * labels under `status.*` in the locale bundles, so the operator reads
 * "In Warteschlange -> Läuft -> Erfolgreich" and not a fifth new word for
 * something they have seen on every other screen (AGENTS.md section 9.1).
 */
export const zComputationRequestStatus = z.enum([
    'queued',
    'running',
    'success',
    'failed'
]);

/**
 * ComputationRequestSummary
 */
export const zComputationRequestSummary = z.object({
    claimed_at: z.iso.datetime({ offset: true }).nullish(),
    computation_run_ids: z.array(z.string()),
    error_summary: z.string().nullish(),
    finished_at: z.iso.datetime({ offset: true }).nullish(),
    id: z.string(),
    ingest_job_id: z.string().nullish(),
    requested_at: z.iso.datetime({ offset: true }),
    requested_by: z.string(),
    status: zComputationRequestStatus
});

/**
 * ComputationRequestListResponse
 */
export const zComputationRequestListResponse = z.object({
    limit: z.int(),
    offset: z.int(),
    requests: z.array(zComputationRequestSummary)
});

/**
 * ComputationRunStatus
 */
export const zComputationRunStatus = z.enum([
    'running',
    'success',
    'failed'
]);

/**
 * ComputationRunSummary
 */
export const zComputationRunSummary = z.object({
    error_detail: z.record(z.string(), z.unknown()).nullish(),
    error_summary: z.string().nullish(),
    finished_at: z.iso.datetime({ offset: true }).nullish(),
    id: z.string(),
    input_event_count: z.int().nullish(),
    job_name: z.string(),
    metric_dates: z.array(z.string()),
    output_row_counts: z.record(z.string(), z.int()),
    started_at: z.iso.datetime({ offset: true }),
    status: zComputationRunStatus
});

/**
 * ComputationRunListResponse
 */
export const zComputationRunListResponse = z.object({
    limit: z.int(),
    offset: z.int(),
    runs: z.array(zComputationRunSummary)
});

/**
 * ConfigurationBlockReason
 *
 * Why a check is not `ok`. The UI keys its localized sentence off this.
 */
export const zConfigurationBlockReason = z.enum([
    'tenant_config_absent',
    'tenant_config_invalid',
    'matrix_approval_pending_legal',
    'zone2_k_not_set',
    'suppressed_not_explained_by_matrix',
    'self_exclusion_source_absent',
    'multiple_brands_declared',
    'commission_chain_not_declared',
    'commission_chain_not_reconstructable',
    'commission_chain_deductions_not_isolated',
    'commission_chain_outside_tolerance',
    'commission_chain_declaration_inconsistent'
]);

/**
 * ConfigurationCheckName
 */
export const zConfigurationCheckName = z.enum([
    'tenant_config',
    'matrix_approval',
    'zone2_k_anonymity',
    'suppressed_accounting',
    'self_exclusion_source',
    'coverage_population',
    'commission_chain'
]);

/**
 * ConfigurationCheckState
 *
 * How one configuration-level check came out.
 *
 * `warning` arrived with the commission-chain check (#691) and is the first
 * state that states a finding while holding nothing back. The founder fixed
 * the semantics on #6 (`#issuecomment-5509796651`): a declared commission
 * chain that does not close against the operator's own claims report is
 * ACCEPT AND WARN, never a rejection -- so it ranks below `blocked` in
 * `rollup`, because a condition that stops work outranks one that only
 * reports. Where it ranks against `unknown` is argued on `rollup` itself.
 */
export const zConfigurationCheckState = z.enum([
    'ok',
    'blocked',
    'warning',
    'unknown'
]);

/**
 * CommissionChainMonthCheck
 *
 * One `(platform x month)` group: declared beside reconstructed.
 */
export const zCommissionChainMonthCheck = z.object({
    claim_month: z.iso.date(),
    claims_compared: z.int(),
    declared_admin_fee_percent: z.string().regex(/^-?\d+\.\d{2,8}$/).nullish(),
    difference_pp: z.string().regex(/^-?\d+\.\d{2,8}$/).nullish(),
    reason: zConfigurationBlockReason.nullish(),
    reconstructed_admin_fee_percent: z.string().regex(/^-?\d+\.\d{2,8}$/).nullish(),
    state: zConfigurationCheckState
});

/**
 * CommissionChainCheck
 *
 * The chain verdict for one affiliate platform.
 *
 * `state` reuses `ConfigurationCheckState` rather than minting a second
 * closed set, so the upload response and the ingest-health panel say the same
 * word for the same fact and one pair of DE/EN labels covers both. `blocked`
 * is representable and never produced here: nothing about a claims upload is
 * ever held back on this check.
 */
export const zCommissionChainCheck = z.object({
    declared_admin_fee_percent: z.string().regex(/^-?\d+\.\d{2,8}$/).nullish(),
    declared_revenue_basis: z.string().nullish(),
    months: z.array(zCommissionChainMonthCheck).optional().default([]),
    reason: zConfigurationBlockReason.nullish(),
    source_platform: z.string(),
    state: zConfigurationCheckState,
    tolerance_pp: z.string().regex(/^-?\d+\.\d{2,8}$/)
});

/**
 * AffiliateClaimUploadResponse
 */
export const zAffiliateClaimUploadResponse = z.object({
    accepted: z.int(),
    chain_check: zCommissionChainCheck,
    ignored_columns: z.array(z.string()).optional().default([]),
    rejected: z.array(zClaimRowRejection),
    total: z.int(),
    unmapped_columns: z.array(z.string()).optional().default([])
});

/**
 * CommissionChainMonthFinding
 *
 * One `(platform x month)` group's two sides, as health serves them.
 *
 * The founder's decision on #6 asks for "a warning-level finding on the claims
 * upload and in pipeline health that NAMES BOTH SIDES: declared vs
 * reconstructed revenue basis and admin fee". The upload response says it for
 * the platform that was just uploaded and is then gone; without this the
 * numbers existed on no GET at all, and health could only say that something
 * disagreed.
 *
 * `reconstructed_admin_fee_percent` and `difference_pp` are null exactly when
 * no reconstruction was performed -- an unusable denominator, or a chain whose
 * deductions BetQ cannot isolate. A null here is the honest shape of "we did
 * not compute this", never a zero.
 *
 * There is no `reconstructed_revenue_basis`, and its absence is the finding
 * rather than an omission: nothing in a claims report reconstructs the basis,
 * because which column holds which step of the chain is open item 4b's
 * per-platform semantic mapping. `declared_revenue_basis` is therefore
 * carried alone, and where the basis is what makes the fee unreachable the
 * month says so through `reason`.
 */
export const zCommissionChainMonthFinding = z.object({
    claim_month: z.iso.date(),
    claims_compared: z.int(),
    declared_admin_fee_percent: z.string().nullish(),
    declared_revenue_basis: z.string().nullish(),
    difference_pp: z.string().nullish(),
    reason: zConfigurationBlockReason.nullish(),
    reconstructed_admin_fee_percent: z.string().nullish(),
    state: zConfigurationCheckState
});

/**
 * CommissionChainPlatformFinding
 *
 * One affiliate platform's chain verdict, per month, on health.
 */
export const zCommissionChainPlatformFinding = z.object({
    declared_admin_fee_percent: z.string().nullish(),
    declared_revenue_basis: z.string().nullish(),
    months: z.array(zCommissionChainMonthFinding).optional().default([]),
    reason: zConfigurationBlockReason.nullish(),
    source_platform: z.string(),
    state: zConfigurationCheckState,
    tolerance_pp: z.string()
});

/**
 * CsvFindingLevel
 *
 * What the operator should DO about a finding (issue #125, decision 5).
 *
 * The act / check / nothing distinction is the part that must survive into the
 * DE+EN copy; a vocabulary an operator cannot tell apart at a glance has failed
 * however precise the constants are.
 *
 * * `error`   -- the file was not accepted; fix it and upload again.
 * * `warning` -- accepted, but BetQ may be ignoring data you meant it to read.
 * * `info`    -- accepted; this is simply what BetQ read and did not read.
 *
 * `error` is not produced today: a hard failure rejects the file before a job
 * row exists. It is in the vocabulary because the level names what the operator
 * acts on, and a set that cannot express "not accepted" would push the next
 * author to invent a second one.
 */
export const zCsvFindingLevel = z.enum([
    'error',
    'warning',
    'info'
]);

/**
 * CsvHeaderFindingKind
 *
 * The closed set of header observations, rendered in the Operator UI.
 *
 * Every value needs DE+EN labels; `app/api/tests/test_locale_parity.py` pins
 * that, so adding a member here without adding both labels fails the suite
 * rather than shipping English into the German UI (`AGENTS.md` §9.1).
 */
export const zCsvHeaderFindingKind = z.enum([
    'unmapped_column',
    'absent_optional_column',
    'absent_conditional_column',
    'likely_renamed_column',
    'declared_ignored_column',
    'stale_ignored_column',
    'declared_skipped_rows',
    'stale_skip_rule',
    'unmapped_game_category'
]);

/**
 * CsvHeaderFinding
 *
 * One observation about a file's header. Reported, never enforced.
 *
 * `related_column` carries the other half of a `likely_renamed_column` pair and
 * is null for every other kind.
 *
 * `value` and `occurrence_count` (issue #726) carry the two things a
 * value-level finding needs and a column-level one never did: WHICH value BetQ
 * could not classify and HOW MANY rows carried it. Without the count the
 * finding cannot distinguish one stray row from a feed BetQ is misreading
 * wholesale, which is the difference between "ignore this" and "fix your
 * mapping"; the job's own counters cannot supply it, because they count rows,
 * not values. Both are null for every kind that describes a column.
 */
export const zCsvHeaderFinding = z.object({
    column: z.string(),
    kind: zCsvHeaderFindingKind,
    level: zCsvFindingLevel,
    occurrence_count: z.int().nullish(),
    related_column: z.string().nullish(),
    value: z.string().nullish()
});

/**
 * CsvMappingErrorCode
 *
 * Machine-readable reason a mapping is not ready.
 *
 * The UI keys a localized explanation off this code; `error` stays as the
 * raw technical detail for API consumers and logs.
 */
export const zCsvMappingErrorCode = z.enum([
    'file_not_found',
    'path_outside_roots',
    'source_mismatch',
    'contract_invalid',
    'unreadable'
]);

/**
 * ClaimsMappingSummary
 *
 * One affiliate platform's claims mapping, as the Mapping Console reads it.
 *
 * Issue #379, the read half. `TenantClaimsMappingResolver.resolve()` already
 * loads the declared contract for an upload, but nothing ever ASKED whether a
 * declared contract loads -- so an operator who added a `claims_sources[]`
 * entry learned the answer by uploading a file and reading an error, which is
 * the onboarding friction the declared-mapping machinery exists to remove.
 *
 * A sibling list rather than more rows in `mappings`, and the type system is
 * what forces it: `CsvMappingSummary.source` is an `AdapterSource`, while a
 * claims source is keyed by the free-form `source_platform` the upload form
 * carries and `affiliate_monthly_claims` stores. `tenant_config.py` states
 * the same separation on `ClaimsSource` -- the affiliate EVENT feed already
 * owns `AdapterSource.affiliate_platform`, and a monthly statement is not an
 * event (open item 4b).
 *
 * Declared HERE, next to `CsvMappingSummary`, rather than in
 * `domain/claims_mapping.py` where the contract itself lives: that module
 * imports `normalise_header_name` from this one, so the reverse import is a
 * cycle. The dependency direction is real (claims mapping reuses the event
 * contract's header normalisation), so the DTO follows the response model it
 * is a field of.
 */
export const zClaimsMappingSummary = z.object({
    delimiter: z.string().nullish(),
    encoding: z.string().nullish(),
    error: z.string().nullish(),
    error_code: zCsvMappingErrorCode.nullish(),
    ignored_columns: z.array(z.string()).optional(),
    mapped_columns: z.array(z.string()).optional(),
    mapping_contract: z.string().nullish(),
    searched_roots: z.array(z.string()).optional(),
    source_platform: z.string(),
    status: zClaimsMappingStatus
});

/**
 * CsvMappingStatus
 */
export const zCsvMappingStatus = z.enum([
    'ready',
    'missing',
    'invalid'
]);

/**
 * CsvMappingSummary
 */
export const zCsvMappingSummary = z.object({
    date_format: z.string().nullish(),
    decimal_separator: z.string().nullish(),
    delimiter: z.string().nullish(),
    error: z.string().nullish(),
    error_code: zCsvMappingErrorCode.nullish(),
    has_header: z.boolean().nullish(),
    ignored_columns: z.array(z.string()).optional(),
    mapped_columns: z.array(z.string()).optional(),
    mapping_contract: z.string(),
    searched_roots: z.array(z.string()).optional(),
    source: zAdapterSource,
    status: zCsvMappingStatus,
    timezone_assumption: z.string().nullish()
});

/**
 * CsvPreviewRejection
 */
export const zCsvPreviewRejection = z.object({
    event_id: z.string().nullish(),
    payload: z.record(z.string(), z.unknown()),
    reason: z.string(),
    row_number: z.int().nullish()
});

/**
 * CsvPreviewResponse
 */
export const zCsvPreviewResponse = z.object({
    accepted: z.int(),
    findings: z.array(zCsvHeaderFinding),
    rejected: z.array(zCsvPreviewRejection),
    rejected_count: z.int(),
    sample_events: z.array(z.record(z.string(), z.unknown())),
    skipped: z.int().optional().default(0),
    total: z.int()
});

/**
 * DailyAffiliateFeatureRow
 */
export const zDailyAffiliateFeatureRow = z.object({
    active_player_count_30d: z.int(),
    affiliate_age_days: z.int(),
    affiliate_bonus_ggr_delta: z.string(),
    affiliate_bonus_ggr_delta_source: z.string(),
    affiliate_bonus_hunter_share: z.string(),
    affiliate_claimed_ftd_delta: z.string(),
    affiliate_click_to_ftd_conversion: z.string(),
    affiliate_cost_mom_growth: z.string(),
    affiliate_cost_mom_growth_source: z.string(),
    affiliate_deal_active_since: z.iso.date().nullable(),
    affiliate_deal_type: z.string().nullable(),
    affiliate_deal_type_source: z.string(),
    affiliate_device_cluster_score: z.string(),
    affiliate_geo_concentration: z.string(),
    affiliate_geo_concentration_source: z.string(),
    affiliate_id: z.string(),
    affiliate_ip_concentration: z.string(),
    affiliate_ltv: z.string(),
    affiliate_nco_balance: z.string(),
    affiliate_ngr_mom_growth: z.string(),
    affiliate_ngr_mom_growth_source: z.string(),
    affiliate_quality_percentile: z.string().nullable(),
    affiliate_quality_recovery_periods: z.int(),
    affiliate_registration_growth_mom: z.string(),
    affiliate_registration_growth_mom_source: z.string(),
    affiliate_retention_d7: z.string(),
    affiliate_retention_d7_baseline: z.string(),
    affiliate_retention_d7_drop_percent: z.string(),
    affiliate_retention_d7_source: z.string(),
    affiliate_revenue_volatility: z.string(),
    affiliate_second_deposit_rate_30d: z.string(),
    affiliate_time_to_first_ftd_days: z.int().nullable(),
    affiliate_whale_share: z.string(),
    bonus_claim_ratio_30d: z.string(),
    bonus_grant_count_30d: z.int(),
    brand: z.string().nullish(),
    click_count_30d: z.int(),
    computed_at: z.iso.datetime({ offset: true }),
    deposit_amount_30d: z.string(),
    deposit_count_30d: z.int(),
    event_count_30d: z.int(),
    ftd_conversion_30d: z.string(),
    ftd_count_30d: z.int(),
    metric_date: z.iso.date(),
    ngr_amount_30d: z.string(),
    registration_count_30d: z.int(),
    sample_guard_detail: z.record(z.string(), z.unknown()),
    sample_guard_passed: z.boolean()
});

/**
 * DailyAffiliateFeaturesResponse
 */
export const zDailyAffiliateFeaturesResponse = z.object({
    features: z.array(zDailyAffiliateFeatureRow),
    limit: z.int(),
    offset: z.int()
});

/**
 * DailyAffiliateMetricRow
 */
export const zDailyAffiliateMetricRow = z.object({
    affiliate_id: z.string(),
    bet_count: z.int(),
    bonus_convert_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    bonus_convert_count: z.int(),
    bonus_cost_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    bonus_cost_source: z.string(),
    bonus_grant_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    bonus_grant_count: z.int(),
    brand: z.string().nullish(),
    chargeback_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    click_count: z.int(),
    computed_at: z.iso.datetime({ offset: true }),
    deposit_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    deposit_count: z.int(),
    event_count: z.int(),
    ftd_count: z.int(),
    ggr_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    ltv_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    metric_date: z.iso.date(),
    ngr_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    payment_fee_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    player_count: z.int(),
    provider_fee_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    registration_count: z.int(),
    stake_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    tax_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    win_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    withdrawal_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    withdrawal_count: z.int()
});

/**
 * DailyAffiliateMetricsResponse
 */
export const zDailyAffiliateMetricsResponse = z.object({
    limit: z.int(),
    metrics: z.array(zDailyAffiliateMetricRow),
    offset: z.int()
});

/**
 * DailyFinancialMetricRow
 */
export const zDailyFinancialMetricRow = z.object({
    active_affiliate_count: z.int(),
    active_player_count: z.int(),
    bet_count: z.int(),
    bonus_convert_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    bonus_convert_count: z.int(),
    bonus_cost_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    bonus_cost_source: z.string(),
    bonus_grant_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    bonus_grant_count: z.int(),
    chargeback_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    computed_at: z.iso.datetime({ offset: true }),
    deposit_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    deposit_count: z.int(),
    event_count: z.int(),
    ftd_count: z.int(),
    ggr_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    ggr_by_category: z.record(z.string(), z.string().regex(/^-?\d+\.\d{2,8}$/)),
    metric_date: z.iso.date(),
    ngr_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    payment_fee_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    provider_fee_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    stake_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    tax_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    win_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    withdrawal_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    withdrawal_count: z.int()
});

/**
 * DailyFinancialMetricsResponse
 */
export const zDailyFinancialMetricsResponse = z.object({
    limit: z.int(),
    metrics: z.array(zDailyFinancialMetricRow),
    offset: z.int()
});

/**
 * DailyPlayerFeatureRow
 */
export const zDailyPlayerFeatureRow = z.object({
    account_age_days: z.int(),
    active_days_30d: z.int(),
    active_days_7d: z.int(),
    bet_count_30d: z.int(),
    bonus_claim_ratio_30d: z.string(),
    bonus_grant_count_30d: z.int(),
    brand: z.string().nullish(),
    campaign_engagement: z.string(),
    computed_at: z.iso.datetime({ offset: true }),
    days_since_last_activity: z.int(),
    days_since_last_deposit: z.int().nullable(),
    days_since_last_withdrawal: z.int().nullable(),
    deposit_amount_30d: z.string(),
    deposit_amount_7d: z.string(),
    deposit_amount_avg_30d: z.string(),
    deposit_amount_avg_trend: z.string(),
    deposit_count_30d: z.int(),
    deposit_count_7d: z.int(),
    deposit_count_lifetime: z.int(),
    deposit_frequency_trend: z.string(),
    event_count_30d: z.int(),
    event_count_7d: z.int(),
    ftd_count_lifetime: z.int(),
    game_variety: z.int().nullable(),
    lifecycle_stage: z.string(),
    maturity_guard_detail: z.record(z.string(), z.unknown()),
    maturity_guard_passed: z.boolean(),
    metric_date: z.iso.date(),
    missed_preferred_days_consecutive: z.int(),
    net_loss_30d: z.string(),
    net_loss_30d_source: z.string(),
    net_loss_prior_avg_30d: z.string().nullable(),
    net_loss_prior_multiple: z.string().nullable(),
    ngr_amount_30d: z.string(),
    ngr_amount_7d: z.string(),
    player_id: z.string(),
    player_ltv: z.string(),
    preferred_category_share_delta: z.string().nullable(),
    preferred_day_of_week: z.string(),
    preferred_game_category: z.string().nullable(),
    session_duration_avg: z.string(),
    stake_share_by_category: z.record(z.string(), z.string()).nullable(),
    value_tier: z.string(),
    value_tier_trend: z.int(),
    withdrawal_to_ltv_ratio: z.string()
});

/**
 * DailyPlayerFeaturesResponse
 */
export const zDailyPlayerFeaturesResponse = z.object({
    features: z.array(zDailyPlayerFeatureRow),
    limit: z.int(),
    offset: z.int()
});

/**
 * DailyPlayerMetricRow
 */
export const zDailyPlayerMetricRow = z.object({
    bet_count: z.int(),
    bonus_convert_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    bonus_convert_count: z.int(),
    bonus_cost_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    bonus_cost_source: z.string(),
    bonus_grant_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    bonus_grant_count: z.int(),
    brand: z.string().nullish(),
    chargeback_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    computed_at: z.iso.datetime({ offset: true }),
    deposit_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    deposit_count: z.int(),
    event_count: z.int(),
    ftd_count: z.int(),
    ggr_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    ltv_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    metric_date: z.iso.date(),
    ngr_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    payment_fee_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    player_id: z.string(),
    provider_fee_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    stake_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    tax_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    win_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    withdrawal_amount: z.string().regex(/^-?\d+\.\d{2,8}$/),
    withdrawal_count: z.int()
});

/**
 * DailyPlayerMetricsResponse
 */
export const zDailyPlayerMetricsResponse = z.object({
    limit: z.int(),
    metrics: z.array(zDailyPlayerMetricRow),
    offset: z.int()
});

/**
 * DashboardDeltaUnit
 *
 * How to render a tile's delta -- the spec gives each tile its own.
 *
 * `percent` is a relative change (NGR, actives), `absolute` is a plain
 * difference in the value's own unit (first deposits), and `points` is a
 * difference between two percentages (bonus-cost share). They are three
 * different sentences and rendering one as another is a wrong number, not a
 * formatting slip: a share moving 12% -> 13% is +1 point and +8.3 percent.
 */
export const zDashboardDeltaUnit = z.enum([
    'percent',
    'absolute',
    'points'
]);

/**
 * DashboardFtdReconciliation
 *
 * The FTD tile's own check, summed over the tile's window (issue #795).
 *
 * The FTD caption states the product thesis -- a delivered FTD marker is a
 * claim BetQ reconciles against its own derivation, never the count itself --
 * and until now nothing served the check it names. `daily_metrics` has written
 * `daily_financial_metrics.ftd_reconciliation` for every computed day since
 * migration 0012; this is that column, summed over the same days the tile's
 * value is summed over. Nothing is computed here that was not already
 * persisted.
 *
 * COUNTS, not money, so these are integers rather than decimal strings. The
 * surrounding `value`/`delta` are strings because a tile's JSON type must not
 * change with its unit; these three are one shape in every case and share the
 * response with `DashboardTrendPoint.active_player_count`, which is an integer
 * for the same reason.
 *
 * * `derived` is what BetQ counted from the deposit stream itself -- the same
 * accumulator the tile's value is built from (`daily_metrics.py`, where
 * `derived_ftd_by_date` and `financial_buckets[...].ftd_count` are
 * incremented on one branch). So on a window BetQ can speak for, `derived`
 * equals the tile face by construction. That is the point rather than a
 * redundancy: the bubble shows the operator the derivation the caption
 * promises, and the day the two ever part company is the day this field is
 * where it shows.
 * * `claimed` counts the `ftd` markers the operator's own feed delivered.
 * A feed that delivers none reads `0`, and that zero is measured: "BetQ
 * derived N and nobody claimed any" is exactly the comparison the thesis
 * offers.
 * * `mismatches` counts delivered markers whose player's first derived deposit
 * fell on a DIFFERENT day. This is the signal with the most in it: where
 * markers exist at all, `derived` is filtered by the marker population (see
 * `_counts_as_first_deposit`, which uses the marker as a negative signal
 * about window membership), so the date-level disagreement is what the two
 * sources can still differ on.
 */
export const zDashboardFtdReconciliation = z.object({
    claimed: z.int(),
    derived: z.int(),
    mismatches: z.int()
});

/**
 * DashboardGoodDirection
 *
 * Which way is good for this metric, decided by the server.
 *
 * The spec makes the delta line the one sanctioned exception to "text wears
 * ink", with `gooddown` on bonus-cost share: a falling share is green. That is
 * a fact about the metric, not about the layout, so it travels with the metric
 * rather than living in a client-side table that the next tile forgets to
 * extend.
 */
export const zDashboardGoodDirection = z.enum(['up', 'down']);

/**
 * DashboardMetric
 *
 * The four KPI tiles, in the spec's own order.
 *
 * A closed set the UI renders, so AGENTS.md section 9.1 applies: every value
 * carries DE+EN copy, pinned by `test_dashboard_metrics_are_labelled`. The
 * names are BetQ's window vocabulary rather than the tile captions -- the
 * caption is locale copy, this is the key it is looked up by.
 */
export const zDashboardMetric = z.enum([
    'ngr_30d',
    'active_players_30d',
    'first_deposits_30d',
    'bonus_cost_ggr_share_30d'
]);

/**
 * DashboardMetricUnit
 *
 * How to render a tile's value. `money` additionally carries the currency.
 *
 * `integer` rather than the obvious `count`, and NOT a style choice: a
 * `StrEnum` subclasses `str`, so a member named after a string method shadows
 * it -- `count` hides `str.count` and pyright rejects the assignment outright
 * (`api_types`, which is where this was caught). The same trap is waiting for
 * `index`, `format`, `title`, `split` and `strip`, so the rule is worth having
 * in mind rather than the fix: do not name a StrEnum member after a str method.
 *
 * `integer` also says the thing more exactly than `count` did -- money and
 * percent are counts of something too; what separates this unit is that the
 * value renders as a whole number.
 */
export const zDashboardMetricUnit = z.enum([
    'money',
    'integer',
    'percent'
]);

/**
 * DashboardKpi
 *
 * One KPI tile: a value, a delta, and how to read both.
 *
 * `value` and `delta` are decimal STRINGS in every unit, counts included.
 * Money must be a string (AGENTS.md section 4) and a row whose JSON type
 * changes with its unit is a shape every client has to branch on -- so all
 * four read the same way and `unit` says how to render.
 *
 * Both are nullable, and null never means zero:
 *
 * * `value` is null when the tile is undefined -- today only the bonus-cost
 * share, whose denominator is GGR. A screen printing `0%` there would claim
 * the operator gave away nothing when the truth is that nothing was earned.
 * * `delta` is null when the PRIOR window holds no computed day at all (a
 * tenant younger than its own comparison), and additionally when the prior
 * denominator is zero, because a relative change from zero is undefined.
 * `current - 0` would be arithmetically fine and a false claim: it states
 * the prior window was empty when BetQ simply has no rows for it.
 * * `reconciliation` is null on the three tiles that have none, and on the FTD
 * tile whenever BetQ cannot speak for the whole window -- no computed day at
 * all, or a computed day whose `ftd_reconciliation` was never written
 * (migration 0012's `{}` server default). Summing such a day as zero would
 * report "nothing was claimed that day" where the truth is that BetQ has no
 * reconciliation for it, which is the first-run lie in miniature.
 */
export const zDashboardKpi = z.object({
    delta: z.string().nullable(),
    delta_unit: zDashboardDeltaUnit,
    good_direction: zDashboardGoodDirection,
    metric: zDashboardMetric,
    reconciliation: zDashboardFtdReconciliation.nullable(),
    unit: zDashboardMetricUnit,
    value: z.string().nullable()
});

/**
 * DashboardOpenVerdicts
 *
 * The four verdict rows, as a partition of the live case population.
 *
 * **The overview counts everything; Home counts what needs you.** Altitude 0 is
 * stock-taking, so the GATED population gets a row here precisely where design
 * item D deliberately gives it no tile -- `DASHBOARD_ALTITUDE0_SPEC.md` says
 * "D's deliberate no-compliance-tile", and only that. Duty of care has a tile
 * on item D as well; what differs there is the unit, not the presence.
 *
 * A partition, so nothing is counted twice: `affiliate` and `retention` are the
 * ACTIONABLE cases of each module, `duty_of_care` is `rg_only`, and
 * `compliance_queue` is `pending_review` -- counted by the queue's own counter,
 * so the row and the door it links to cannot disagree.
 *
 * They do not sum to every live row, and that is the same deliberate gap the
 * inbox tabs have: `suppressed` and `player_gate_blocked` have no row here
 * because they are not the operator's decision to make, and `care_recorded` is
 * a closed case rather than somebody waiting.
 *
 * CASES, not entities. Item D's care tile counts PLAYERS behind the door
 * because it is a working screen; this is the verdict distribution, and the two
 * legitimately differ on a tenant where one player holds two care cases.
 */
export const zDashboardOpenVerdicts = z.object({
    affiliate: z.int(),
    compliance_queue: z.int(),
    duty_of_care: z.int(),
    retention: z.int()
});

/**
 * DashboardTrendPoint
 *
 * One day of the NGR chart, plus the active count the hover reveals.
 */
export const zDashboardTrendPoint = z.object({
    active_player_count: z.int(),
    metric_date: z.iso.date(),
    ngr_amount: z.string()
});

/**
 * DashboardWindow
 *
 * The two 30-day windows every KPI is measured over.
 *
 * Anchored on the newest computed `metric_date`, NOT on wall-clock today. A
 * dashboard anchored on today reads a partial or empty window whenever
 * computation is behind -- and the days it has no rows for contribute zero,
 * which is exactly the lie the spec's first-run rule names. Anchoring on the
 * data and STATING the anchor lets the screen say "as of" instead.
 *
 * `days` is echoed rather than assumed so a client never hard-codes 30.
 */
export const zDashboardWindow = z.object({
    days: z.int(),
    end: z.iso.date(),
    prior_end: z.iso.date(),
    prior_start: z.iso.date(),
    start: z.iso.date()
});

/**
 * DealSimulationRequest
 */
export const zDealSimulationRequest = z.object({
    revshare_percent: z.string()
});

/**
 * DealSimulationResponse
 *
 * A deal simulation, and the period it was computed over.
 *
 * `window_days` is a span of CALENDAR DAYS -- the period the money below was
 * summed over, at most the six months the deal simulator is contracted to
 * cover.
 *
 * `sample_size` is NOT a count of players, deposits or days: it counts the
 * non-overlapping 30-day windows sampled to de-overlap the trailing 30-day
 * rolling sums, so a full six-month history yields 6.
 *
 * Both are 0 for an affiliate with no computed history, which this endpoint
 * answers with 200 and zero money rather than with a not-found.
 */
export const zDealSimulationResponse = z.object({
    affiliate_id: z.string(),
    cpa_actual: z.string().regex(/^-?\d+\.\d{2,8}$/),
    operator_savings: z.string().regex(/^-?\d+\.\d{2,8}$/),
    revshare_simulated: z.string().regex(/^-?\d+\.\d{2,8}$/),
    sample_size: z.int(),
    window_days: z.int()
});

/**
 * EntityOrder
 *
 * How the ENTITY page is ordered (#491, Manuel's sort ask via DevBetQ).
 *
 * A closed set rather than a free string, the #466 shape: an unknown value is
 * a 422, never a silent fall-back to the default. A typo that quietly returns
 * the default ordering is the worse failure -- the caller reads a
 * correct-looking page that answers a different question from the one it
 * asked.
 *
 * ENTITY level only. The issue asks for "A>Z/Z>A on both levels", but every
 * case inside a group shares one `entity_ref` by construction, so a row-level
 * alphabet could only mean some other column. The rows inside a group have
 * one order, the headline's (#1009), which this parameter never touches. The
 * page sorts; the rows do not.
 *
 * Display only. The headline within a group stays severity-then-outcome-rank
 * (founder decisions 2026-08-06 / 2026-08-08), and the total is unaffected.
 */
export const zEntityOrder = z.enum([
    'latest',
    'oldest',
    'alpha',
    'alpha_desc'
]);

/**
 * EventRejection
 */
export const zEventRejection = z.object({
    event_id: z.string().nullish(),
    reason: z.string()
});

/**
 * EventBatchResponse
 */
export const zEventBatchResponse = z.object({
    accepted: z.int(),
    findings: z.array(zCsvHeaderFinding),
    ingest_job_id: z.string(),
    rejected: z.array(zEventRejection),
    rejected_count: z.int(),
    skipped: z.int().optional().default(0)
});

/**
 * FreezeResolveRequest
 *
 * A compliance user ends one freeze, or every active one, with a reason.
 *
 * Both fields are stripped and refused when blank, and `min_length=1` alone
 * is why that has to be said. It accepts `"   "`, which then lands in
 * `operator_freeze_state.resolution_reason` as the entire account of why a
 * pipeline gate was lifted -- the same defect `CareRecordRequest._non_blank`
 * exists to prevent on the other audited free-text write, and this one is
 * reachable from a screen since #772.
 *
 * A blank `source` is worse than useless rather than merely empty: `""` is a
 * source no freeze row carries, so `clear_freeze` filters to nothing and
 * returns `{"resolved": 0}` -- a 200 that reads as "there was nothing to
 * clear" when what happened is that the request named a source that cannot
 * exist. Omitting the field (or sending null) is the deliberate way to say
 * "every active freeze", and it stays the only way.
 */
export const zFreezeResolveRequest = z.object({
    reason: z.string().min(1).max(2000),
    source: z.string().nullish()
});

/**
 * FreezeResolveResponse
 */
export const zFreezeResolveResponse = z.object({
    resolved: z.int()
});

/**
 * FreezeStateRow
 *
 * One active Freeze-on-Fail row, as the Ingestion console renders it.
 *
 * `reason_code` is here for the same reason it is on `IngestHealthResponse`
 * (#102): `reason` is `str(exc)` from whatever raised the freeze, and
 * `specs/api_contract.yaml` says in as many words that it "must never be
 * rendered as UI copy". The health tile keys its localized sentence off the
 * code -- but off `active_freeze`, which is the NEWEST active row only, so a
 * tenant frozen on two sources gets one sentence that describes one of them.
 * Once the screen lists the rows (#772) that sentence has to be per row, and
 * the code is the only field it can come from.
 *
 * Optional on the WIRE, though a live API always sends it: the route coalesces
 * a null column to `job_failed`. What the default buys is the deploy skew
 * AGENTS.md section 10 records as normal here -- a tag exists per image, not
 * per commit -- where a frontend pinned ahead of the API meets a response
 * without the field. Required, that fails the generated Zod schema for the
 * whole list, which the Ingestion page reports as a red API-error banner
 * across the entire screen for every compliance user whenever a freeze exists.
 * Optional, `freezeReasonLabel(null)` renders the `job_failed` sentence and
 * the panel still works. The same reasoning as `claims_mappings` on #379.
 */
export const zFreezeStateRow = z.object({
    created_at: z.iso.datetime({ offset: true }),
    id: z.string(),
    reason: z.string(),
    reason_code: z.string().nullish(),
    source: z.string()
});

/**
 * FreezeListResponse
 */
export const zFreezeListResponse = z.object({
    freezes: z.array(zFreezeStateRow).optional()
});

/**
 * GatedByCause
 *
 * Why each live gated case is held, per the configuration in force.
 *
 * Issue #394. `pending_review` has two causes and the screen showed one number
 * for both, so the operator could not tell "waiting on somebody outside this
 * company" from "waiting on your own compliance officer":
 *
 * * `matrix_pending_legal` -- the licence jurisdiction is not signed off, so
 * `effective_verdict` collapses EVERY cell to `requires_review` (`gate.py`).
 * Recording the sign-off releases the whole population at once.
 * * `authored_requires_review` -- the cell says `requires_review` because
 * legal wrote it that way. These wait on nobody outside the tenant; they are
 * the compliance queue as authored, and legal sign-off releases none of them.
 * AuroraPlay's DE row authors four of its seven actions this way.
 * * `stale` -- the case is still held under a cell that no longer asks for a
 * review. Nothing in force justifies it, which makes it the `stale_gate`
 * shape from `SuppressedByCause` one status over: a transition artefact
 * between an approval landing and the affected rows being re-gated.
 *
 * The first two are mutually exclusive by construction, not by convention: the
 * approval gate is jurisdiction-wide, so while it is pending every gated row
 * is the first kind and once it is granted none of them is.
 *
 * Exhaustive by construction -- one if/elif/else over the rows the query
 * returned, no membership test and no `continue` -- so the three fields always
 * sum to `GatedAccounting.total`. That sum is what makes the census checkable.
 *
 * An action the matrix defines no cell for lands in `stale`: `matrix_cell`
 * answers `blocked` for an unlisted action, which is not `requires_review`
 * either, so nothing in force justifies the hold. It is deliberately NOT the
 * fourth bucket its suppressed counterpart has -- this census answers one
 * question, "held with nothing in force behind it", and a transition artefact
 * does not need two lines to say so.
 */
export const zGatedByCause = z.object({
    authored_requires_review: z.int(),
    matrix_pending_legal: z.int(),
    stale: z.int()
});

/**
 * GatedAccounting
 *
 * The live `Awaiting compliance review` population, and what explains it.
 *
 * `total` is an independent count over the same statuses the gated tab sums
 * (`INBOX_GATED_STATUSES`), so the census cannot drift from the number it
 * explains. It needs no tenant_config and is therefore present on every path.
 *
 * `by_cause` is null exactly when classification was impossible: no stored
 * tenant_config, or one that does not parse. Null means "nothing could explain
 * these", never zero -- inventing a cause for a population is the failure the
 * issue was filed about. Whenever it is non-null, its three fields sum to
 * `total`.
 */
export const zGatedAccounting = z.object({
    by_cause: zGatedByCause.nullish(),
    total: z.int()
});

/**
 * HomeOutcomeCount
 *
 * One entry of a tile's split line: `1 cut`, `2 investigate`.
 *
 * `outcome` is null for an entity whose leading case carries no stored
 * `recommendation_outcome` -- the same gap `RecommendationEntityHeadline`
 * already reports, labelled by the UI rather than dropped, because dropping it
 * would make the split stop summing to the tile it explains.
 *
 * Ordered by the founder's outcome rank, worst first, so the line reads in
 * precedence order without the client re-sorting it.
 */
export const zHomeOutcomeCount = z.object({
    entity_count: z.int(),
    outcome: z.string().nullable()
});

/**
 * HomeModuleTile
 *
 * One module tile: how many entities, and what leads them.
 *
 * `entity_count` is the number of entities this tile's own filter shows, so
 * pressing the tile cannot produce a list of a different length. `outcomes`
 * sums to it by construction: one leading case per entity, counted once.
 */
export const zHomeModuleTile = z.object({
    entity_count: z.int(),
    module: z.string(),
    outcomes: z.array(zHomeOutcomeCount)
});

/**
 * HomeStrip
 *
 * The day, stated above the feed (#432).
 *
 * Every number here is server-stated. The spec forbids client counting for the
 * reason `RecommendationEntityGroup.case_count` already records: a client
 * counting an array it was not sent in full prints a number that is quietly
 * wrong, and this feed is deliberately served short.
 *
 * `entity_count` is NOT the sum of the module tiles. An entity holding cases
 * in both modules is one entity and two tile members; the total counts it
 * once, which is what makes it the count of the feed rather than of the tiles.
 *
 * Unaffected by the `module` filter, deliberately -- "the strip states the
 * day, the filter states the view" (founder + product, 2026-08-11).
 */
export const zHomeStrip = z.object({
    duty_of_care_player_count: z.int(),
    entity_count: z.int(),
    modules: z.array(zHomeModuleTile),
    open_case_count: z.int()
});

/**
 * IdentityFlagState
 *
 * Whether the open identity-flag backlog is asking for a human (issue #9).
 *
 * Founder decision 2026-08-08 on #9: *"any open identity flag (count != 0) is
 * an ops-attention signal, so the first conflict cannot arrive silently"*, with
 * question 5 -- what open-flag count is unhealthy -- answered as "non-zero is
 * unhealthy". The threshold is therefore not a tunable: it is the decision.
 *
 * Two states, not three. `unknown` earns its place on `ConfigurationCheckState`
 * because a config that cannot be read leaves the answer genuinely unavailable;
 * the flag count is a `COUNT(*)` over one indexed table on the same connection
 * that answered everything else in this response, so there is no path on which
 * it is unknown.
 */
export const zIdentityFlagState = z.enum(['ok', 'attention']);

/**
 * InboxTab
 *
 * The three-way inbox split, as a SERVER-side parameter (#78).
 *
 * Deliberately a tab rather than a repeatable `status`. Both express the same
 * query, but only this one makes the duty-of-care leak impossible rather than
 * merely tested: a client cannot compose a tab that includes `rg_only`,
 * because it does not compose the tab at all. Register item 27 forbids
 * duty-of-care in a commercial tab *or its count*, and the cheapest way to
 * honour a rule is to remove the ability to break it.
 *
 * It is also additive. Widening `status` from `string` to `array<string>`
 * narrows the published schema for every existing consumer -- the
 * `openapi_breaking_diff` gate flagged exactly that -- while `tab` is a new
 * optional parameter and breaks nothing.
 */
export const zInboxTab = z.enum([
    'actionable',
    'gated',
    'closed'
]);

/**
 * IngestFindingRow
 *
 * One thing BetQ noticed about this run and did not act on (issue #125).
 *
 * Its own endpoint rather than a field on the job summary, mirroring
 * rejections: the job LIST would otherwise load every run's findings to render
 * a table that shows none of them.
 *
 * Usually a column BetQ did not read. Since issue #726 a finding can also
 * describe a VALUE -- a `game_category` outside the canonical vocabulary -- in
 * which case `value` and `occurrence_count` carry it and `column_name` names
 * the envelope field rather than a CSV header.
 *
 * Since issue #773 the row also says whether it is NEWS. A finding is correct
 * on every upload, so a standing mapping problem and a one-off read
 * identically; `prior_job_count` and `first_seen_at` are what tells them
 * apart, and they are computed from the operator's own earlier findings
 * rather than stored.
 */
export const zIngestFindingRow = z.object({
    column_name: z.string(),
    created_at: z.iso.datetime({ offset: true }),
    first_seen_at: z.iso.datetime({ offset: true }).nullable(),
    id: z.int(),
    ingest_job_id: z.string(),
    kind: zCsvHeaderFindingKind,
    level: zCsvFindingLevel,
    occurrence_count: z.int().nullish(),
    prior_job_count: z.int(),
    related_column: z.string().nullish(),
    value: z.string().nullish()
});

/**
 * IngestFindingListResponse
 */
export const zIngestFindingListResponse = z.object({
    findings: z.array(zIngestFindingRow),
    limit: z.int(),
    offset: z.int()
});

/**
 * IngestHealthSource
 */
export const zIngestHealthSource = z.object({
    latest_event_time: z.iso.datetime({ offset: true }).nullish(),
    record_count: z.int(),
    source: z.string(),
    status: z.string()
});

/**
 * IngestJobSummary
 */
export const zIngestJobSummary = z.object({
    accepted_count: z.int(),
    created_at: z.iso.datetime({ offset: true }),
    error_summary: z.string().nullish(),
    finished_at: z.iso.datetime({ offset: true }),
    id: z.string(),
    probably_killed: z.boolean(),
    rejected_count: z.int(),
    skipped_count: z.int().optional().default(0),
    source_adapter: z.string(),
    status: z.string(),
    total_count: z.int()
});

/**
 * IngestJobListResponse
 */
export const zIngestJobListResponse = z.object({
    jobs: z.array(zIngestJobSummary),
    limit: z.int(),
    offset: z.int(),
    total: z.int()
});

/**
 * IngestRejectionRow
 */
export const zIngestRejectionRow = z.object({
    event_id: z.string().nullish(),
    id: z.int(),
    ingest_job_id: z.string().nullish(),
    payload: z.record(z.string(), z.unknown()),
    reason: z.string(),
    received_at: z.iso.datetime({ offset: true })
});

/**
 * IngestRejectionListResponse
 */
export const zIngestRejectionListResponse = z.object({
    limit: z.int(),
    offset: z.int(),
    rejections: z.array(zIngestRejectionRow)
});

/**
 * IngestSourceSummary
 */
export const zIngestSourceSummary = z.object({
    accepted_count: z.int().optional().default(0),
    job_count: z.int().optional().default(0),
    latest_event_time: z.iso.datetime({ offset: true }).nullish(),
    latest_job_finished_at: z.iso.datetime({ offset: true }).nullish(),
    latest_job_id: z.string().nullish(),
    latest_job_status: z.string().nullish(),
    record_count: z.int().optional().default(0),
    rejected_count: z.int().optional().default(0),
    skipped_count: z.int().optional().default(0),
    source: z.string()
});

/**
 * IngestSourcesResponse
 */
export const zIngestSourcesResponse = z.object({
    sources: z.array(zIngestSourceSummary).optional()
});

/**
 * JurisdictionClassState
 *
 * One row's regulatory class, beside the BetQ default it may deviate from.
 *
 * Both values, never just the effective one. The founder's 2026-09-02
 * addendum makes deviation from the compliant default a warned state
 * ("Warnings would be good idea to add"), and a screen cannot warn about a
 * deviation it cannot see: with only `effective` on the wire the UI would have
 * to carry its own copy of the register to know whether the operator moved
 * anything, and two copies of a legal fact eventually disagree.
 *
 * Deliberately NOT inside `ComplianceMatrixLocked`. The class is editable --
 * founder decision 2026-09-02, over the desk's own recommendation of a
 * read-only badge -- so putting it beside `pseudonymize_player_refs` and the
 * k-value would tell the screen the opposite of what was decided.
 */
export const zJurisdictionClassState = z.object({
    betq_default: z.string(),
    effective: z.string(),
    overridden: z.boolean()
});

/**
 * MatrixApprovalState
 *
 * Legal sign-off for one jurisdiction, as the Action matrix panel reads it.
 *
 * This was a bare status string. The panel cast the map to an object and read
 * `status` and `approval_ref` off it, so both were `undefined` and both
 * fallbacks fired: every jurisdiction rendered "Legal approval pending" with
 * reference "-" forever, whether or not legal had signed it off (issue #100).
 * An approved row was indistinguishable from an unapproved one on the one
 * screen whose job is to tell them apart.
 *
 * `approved_by` and `approved_at` ride along because they were readable
 * nowhere outside the database -- no endpoint returned them, so an approval's
 * provenance could not be audited over the API at all. `notes` deliberately
 * does not: it is free legal text with no consumer.
 *
 * `approved_at` is when BetQ RECORDED the row, not when legal signed it off:
 * `approve_matrix_row` stamps `datetime.now(UTC)` and the request carries no
 * date. Open item 16 documents the gap with a worked example -- a row approved
 * 2026-06-24 stores `approved_at 2026-07-23` -- and the UI labels the column
 * "Recorded on" for exactly this reason. Anything that needs the true sign-off
 * date needs a new field the approver supplies (issue #5); do not read this
 * one as one.
 */
export const zMatrixApprovalState = z.object({
    approval_ref: z.string().nullish(),
    approved_at: z.iso.datetime({ offset: true }).nullish(),
    approved_by: z.string().nullish(),
    status: z.string()
});

/**
 * ComplianceMatrixLocked
 *
 * The read-only half of the matrix response.
 *
 * Typed rather than `dict[str, Any]`: the untyped dict is why #100 survived on
 * both sides at once. The payload could drop a field the UI read without
 * FastAPI, mypy, `tsc` or lint noticing on either end.
 */
export const zComplianceMatrixLocked = z.object({
    matrix_approvals: z.record(z.string(), zMatrixApprovalState),
    min_group_size_k: z.int().nullable(),
    pseudonymize_player_refs: z.boolean()
});

/**
 * ComplianceMatrixResponse
 */
export const zComplianceMatrixResponse = z.object({
    care_action_types: z.array(z.string()),
    classes: z.record(z.string(), zJurisdictionClassState),
    jurisdictions: z.array(z.string()),
    locked: zComplianceMatrixLocked,
    matrix: z.record(z.string(), z.record(z.string(), z.string()))
});

/**
 * MatrixCellChange
 *
 * One cell whose verdict moved between two consecutive config revisions.
 *
 * `old_value` and `new_value` are nullable because the cell itself can appear
 * or disappear: a jurisdiction added to the matrix has no previous verdict,
 * and an action type removed from a row has no current one. Null is not
 * "unknown" here -- it means the cell was absent at that revision, which the
 * gate reads as `blocked` (`gate.matrix_cell`). Both are rendered through
 * `matrixVerdictLabel`, never raw.
 */
export const zMatrixCellChange = z.object({
    action_type: z.string(),
    jurisdiction: z.string(),
    new_value: z.string().nullable(),
    old_value: z.string().nullable()
});

/**
 * MatrixClassChange
 *
 * One jurisdiction whose EFFECTIVE class moved between two revisions (#632).
 *
 * Effective, not the stored override: an override appearing for the first time
 * moves the row from the register's class, and reporting `null -> unregulated`
 * would hide which class the row actually left. An override written with the
 * same value the register already carries moves nothing and produces no entry.
 *
 * A reclassification is what opens the duty-of-care floor, so it is the one
 * act on this screen that must never be silent. Before #632 it would have been
 * exactly that: `matrix_history` projected only the `jurisdiction_action_matrix`
 * key, so the override map was not even read, and a revision that changed
 * nothing else produced an empty diff and was dropped.
 */
export const zMatrixClassChange = z.object({
    jurisdiction: z.string(),
    new_class: z.string(),
    old_class: z.string()
});

/**
 * MatrixChangelogEntry
 *
 * One revision that moved at least one matrix cell (#632, slice 3).
 *
 * `changed_at` is the changelog row's write time, which for this trail IS the
 * moment the edit happened -- unlike `MatrixApprovalState.approved_at`, which
 * records when BetQ wrote down a sign-off that happened elsewhere. The two
 * must not be read as the same kind of timestamp.
 *
 * There is deliberately no actor field. `tenant_config_changelog.changed_by`
 * is stored on every row and stays stored, but it is not returned: on the
 * platform it is an actor_ref (`user:<uuid>`) that nothing resolves to a name,
 * and the pilot's own approval acts carry a free-typed `--changed-by` string
 * instead. Founder ruling on #632, 2026-08-30: ship `cell / when / old -> new
 * / reason` and defer "who" to issue #5's resolvable identity, rather than
 * render an opaque id or invent a generic label no contract specifies. The
 * column is addable retroactively over the whole history once #5 lands.
 */
export const zMatrixChangelogEntry = z.object({
    change_reason: z.string().nullable(),
    changed_at: z.iso.datetime({ offset: true }),
    changes: z.array(zMatrixCellChange),
    class_changes: z.array(zMatrixClassChange),
    revision: z.int()
});

/**
 * ComplianceMatrixHistoryResponse
 *
 * Matrix diffs only -- never the stored config snapshots they came from.
 *
 * Newest revision first. A revision that moved neither a cell nor a
 * jurisdiction class is absent entirely: `apply_config` appends a row for every
 * config write, so most revisions on a live tenant are calibration or mapping
 * changes that this screen must not report as policy movement.
 */
export const zComplianceMatrixHistoryResponse = z.object({
    entries: z.array(zMatrixChangelogEntry)
});

/**
 * NotificationDispatchOrder
 *
 * Which end of the dispatch table a reader wants first.
 *
 * Two callers, two opposite needs, and conflating them is what broke the
 * drain (#104). The Operator UI's delivery table shows the most recent
 * activity, so `newest_first` stays the default and no existing caller sees a
 * different page than before. The delivery loop needs the opposite: it takes
 * a page, delivers it, and comes back -- so it must start at the oldest
 * undelivered row or the tail of the queue starves behind every newly written
 * case.
 *
 * Explicit rather than implied by `?status=queued`, deliberately: ordering
 * that changes as a side effect of a filter is invisible at the call site,
 * and a reader of either caller's URL should not have to know a rule to
 * predict what comes back.
 */
export const zNotificationDispatchOrder = z.enum(['newest_first', 'oldest_first']);

/**
 * NotificationDispatchRow
 */
export const zNotificationDispatchRow = z.object({
    channel: z.string(),
    created_at: z.iso.datetime({ offset: true }),
    error_summary: z.string().nullable(),
    id: z.uuid(),
    payload: z.record(z.string(), z.unknown()),
    recommendation_id: z.uuid(),
    route: z.string(),
    sent_at: z.iso.datetime({ offset: true }).nullable(),
    status: z.string()
});

/**
 * NotificationDispatchStatus
 *
 * The five states a delivery work item can hold.
 *
 * Declared for the same reason `RecommendationStatus` was: they were bare
 * strings across `recommendations/writer.py` (`queued`),
 * `recommendations/regate.py` (`cancelled`) and
 * `recommendations/repository.py` (`sent`, `failed`), so nothing told a reader
 * they existed or that the set was closed. As a query-parameter type it also
 * turns a mistyped `?status=` into a `422` instead of a `200` carrying an
 * empty list -- which, on the endpoint the delivery loop polls, would read as
 * "the queue is empty" rather than "you asked the wrong question".
 *
 * Only `queued` is deliverable. `sent` and `failed` are both terminal reports
 * from the delivery adapter (`update_dispatch_status`), and `cancelled` is
 * written by the re-gate path when a matrix change withdraws a queued row.
 * All of them carry DE+EN labels under `status.*` in the locale bundles,
 * because the Operator UI renders this column.
 *
 * `not_for_delivery` is the fifth, added for the #104 demo-minimum brief
 * (founder, 2026-09-19): a case that is not `open` writes its row, and that
 * row is never delivered. It is a new value rather than a reuse of
 * `cancelled` because the two state different facts -- `cancelled` is a row
 * that WAS going out until a matrix change withdrew it, so reading "the
 * compliance alert was cancelled" off a case that was never deliverable
 * would misreport the very audit trail the row exists to be. It is also not
 * called `withheld`: `GET /v1/notifications/dispatches` already answers
 * `withheld: true` with a `withheld_reason` for a whole page while a run is
 * incomplete or the tenant is frozen (#131), and one word covering both
 * "this row" and "this page" is how a query comes to mean something other
 * than what its author read.
 */
export const zNotificationDispatchStatus = z.enum([
    'queued',
    'sent',
    'failed',
    'cancelled',
    'not_for_delivery'
]);

/**
 * NotificationDispatchStatusRequest
 */
export const zNotificationDispatchStatusRequest = z.object({
    error_summary: z.string().max(2000).nullish(),
    status: z.enum(['sent', 'failed'])
});

/**
 * OperatorArchetype
 */
export const zOperatorArchetype = z.enum([
    'regulated_premium_casino',
    'regulated_premium_sportsbook',
    'regulated_mass_casino',
    'regulated_mass_sportsbook',
    'crypto_native_casino',
    'crypto_native_sportsbook'
]);

/**
 * OptInRegateSummary
 *
 * What re-gating the live recommendation rows did, as counts.
 *
 * Reported rather than left to be inferred, on product's condition from open
 * item 21: "a config apply that silently rewrites recommendation rows is the
 * wrong shape even when the rewrite is correct -- this is a mutation of
 * compliance state and it must be visible in the result". A consent upload is
 * the same act through a different door.
 *
 * `rows_regated` counts rows whose stored player-gate verdict was refreshed;
 * `rows_reassigned` is the subset whose STATUS moved with it -- a case newly
 * blocked, or one released because consent came back. The two differ on every
 * sweep, because a verdict may be re-recorded on a row a human has decided on
 * without that decision being overwritten.
 */
export const zOptInRegateSummary = z.object({
    rows_examined: z.int().optional().default(0),
    rows_reassigned: z.int().optional().default(0),
    rows_regated: z.int().optional().default(0)
});

/**
 * OptInRowRejection
 */
export const zOptInRowRejection = z.object({
    code: z.string(),
    reason: z.string(),
    row_number: z.int()
});

/**
 * OptInUploadMode
 *
 * How an upload combines with what is already stored.
 *
 * Explicit rather than inferred, and for the same reason as
 * `AttributionUploadMode`: a partial file uploaded as a `snapshot` drops every
 * player it omits, which is correct for a full monthly export and destructive
 * for a correction. The hazard is identical here because it is the identical
 * file.
 *
 * Deliberately a separate enum rather than a shared one. The Form() default
 * binds into the OpenAPI schema per endpoint, so sharing it would couple two
 * contracts that are free to diverge.
 */
export const zOptInUploadMode = z.enum(['snapshot', 'merge']);

/**
 * Body_upload_player_marketing_optins_v1_players_marketing_optins_upload_post
 */
export const zBodyUploadPlayerMarketingOptinsV1PlayersMarketingOptinsUploadPost = z.object({
    file: z.string(),
    mode: zOptInUploadMode.optional().default('snapshot'),
    source_platform: z.string().optional().default('pam_backend')
});

/**
 * PlayerAttributionItem
 */
export const zPlayerAttributionItem = z.object({
    affiliate_id: z.string().nullable(),
    attribution_month: z.string(),
    player_id: z.string(),
    source_platform: z.string(),
    source_ref: z.string().nullable()
});

/**
 * PlayerAttributionListResponse
 */
export const zPlayerAttributionListResponse = z.object({
    attributions: z.array(zPlayerAttributionItem),
    limit: z.int(),
    offset: z.int()
});

/**
 * PlayerAttributionUploadResponse
 *
 * `unattributed` is a subset of `accepted`, not a separate bucket.
 *
 * It used to count rows the parser threw away; those rows are now stored
 * statements, so it counts how many of the accepted rows say "no affiliate".
 * Reported separately because a de-tag moves money away from an affiliate and
 * an operator should see how many their upload just performed.
 */
export const zPlayerAttributionUploadResponse = z.object({
    accepted: z.int(),
    backdated: zBackdatedRestatement,
    rejected: z.array(zAttributionRowRejection),
    total: z.int(),
    unattributed: z.int()
});

/**
 * PlayerOptInItem
 */
export const zPlayerOptInItem = z.object({
    optin_email: z.boolean().nullable(),
    optin_month: z.string(),
    optin_push: z.boolean().nullable(),
    optin_sms: z.boolean().nullable(),
    player_id: z.string(),
    source_platform: z.string()
});

/**
 * PlayerOptInListResponse
 */
export const zPlayerOptInListResponse = z.object({
    limit: z.int(),
    offset: z.int(),
    optins: z.array(zPlayerOptInItem)
});

/**
 * PlayerOptInUploadResponse
 *
 * `contactable` is a subset of `accepted`, not a separate bucket.
 *
 * It counts the accepted rows with at least one channel explicitly true --
 * the MVP contactability rule (founder decision, 2026-08-15 on #392: a player
 * is contactable if any channel opt-in is true, because no pattern declares a
 * channel to match against). Reported at upload time because it is the number
 * that decides how much of the retention module an operator will see once the
 * gate ships, and they should learn it when they upload rather than later.
 */
export const zPlayerOptInUploadResponse = z.object({
    accepted: z.int(),
    contactable: z.int(),
    regated: zOptInRegateSummary.optional().default({
        rows_examined: 0,
        rows_reassigned: 0,
        rows_regated: 0
    }),
    rejected: z.array(zOptInRowRejection),
    total: z.int()
});

/**
 * RawEventBatchRequest
 *
 * Raw body so one invalid event does not reject the whole batch as 422.
 */
export const zRawEventBatchRequest = z.object({
    events: z.array(z.record(z.string(), z.unknown())).min(1)
});

/**
 * ReasoningBackend
 */
export const zReasoningBackend = z.enum(['hosted_api', 'local_llm']);

/**
 * RecommendationActionRequest
 */
export const zRecommendationActionRequest = z.object({
    note: z.string().max(2000).nullish(),
    reason: z.string().max(2000).nullish()
});

/**
 * RecommendationEntityHeadline
 *
 * The one case that names an entity group (#247, build step 1).
 *
 * Deliberately NOT called a verdict. #247 §3 writes the card as
 * "Verdict: CUT", but `matrix_verdict` already means the jurisdiction
 * matrix's answer on this row, and two things called a verdict in one
 * response is how a compliance fact gets misread.
 *
 * Every field here is copied from a stored row named by `recommendation_id`,
 * so the group header renders no value the flat list does not already hold.
 */
export const zRecommendationEntityHeadline = z.object({
    module: z.string(),
    outcome: z.string().nullable(),
    pattern_id: z.string(),
    recommendation_id: z.uuid(),
    severity: z.string()
});

/**
 * RecommendationEntityImportant
 *
 * The Important mark of one entity, as the set/clear routes echo it (#495).
 *
 * The state, not the write: both routes answer with the same shape, so a
 * client never has to infer what it just did from a status code, and a
 * repeated mark or a repeated clear answers identically to the first one.
 *
 * `entity_ref` is the pseudonymous ref the inbox already groups by, never the
 * raw `entity_id` -- keeping the mark out of open backlog item 41.
 */
export const zRecommendationEntityImportant = z.object({
    entity_ref: z.string(),
    entity_type: z.string(),
    important: z.boolean()
});

/**
 * RecommendationEventRow
 */
export const zRecommendationEventRow = z.object({
    actor_ref: z.string(),
    created_at: z.iso.datetime({ offset: true }),
    event_type: z.string(),
    id: z.int(),
    new_status: z.string().nullable(),
    old_status: z.string().nullable(),
    payload: z.record(z.string(), z.unknown()),
    reason: z.string().nullable()
});

/**
 * RecommendationDetail
 */
export const zRecommendationDetail = z.object({
    action_type: z.string(),
    baseline_comparison: z.record(z.string(), z.unknown()),
    created_at: z.iso.datetime({ offset: true }),
    entity_id: z.string(),
    entity_ref: z.string(),
    entity_type: z.string(),
    events: z.array(zRecommendationEventRow),
    firing_days: z.int(),
    first_seen: z.iso.date(),
    gate_reason: z.string(),
    id: z.uuid(),
    last_pattern_version: z.string(),
    last_seen: z.iso.date(),
    matrix_approval_status: z.string().nullable(),
    matrix_jurisdiction: z.string(),
    matrix_verdict: z.string(),
    metric_date: z.iso.date(),
    module: z.string(),
    narration: z.string().nullable(),
    narration_source: z.string().nullable(),
    notification_state: z.string(),
    pattern_id: z.string(),
    pattern_version: z.string(),
    rationale: z.record(z.string(), z.unknown()),
    recurrence_count: z.int(),
    resolved_at: z.iso.date().nullable(),
    severity: z.string(),
    stage: z.string(),
    status: z.string(),
    superseded_by: z.uuid().nullable(),
    updated_at: z.iso.datetime({ offset: true })
});

/**
 * RecommendationSummary
 */
export const zRecommendationSummary = z.object({
    action_type: z.string(),
    baseline_comparison: z.record(z.string(), z.unknown()),
    created_at: z.iso.datetime({ offset: true }),
    entity_ref: z.string(),
    entity_type: z.string(),
    firing_days: z.int(),
    first_seen: z.iso.date(),
    gate_reason: z.string(),
    id: z.uuid(),
    last_pattern_version: z.string(),
    last_seen: z.iso.date(),
    matrix_approval_status: z.string().nullable(),
    matrix_jurisdiction: z.string(),
    matrix_verdict: z.string(),
    metric_date: z.iso.date(),
    module: z.string(),
    notification_state: z.string(),
    pattern_id: z.string(),
    pattern_version: z.string(),
    rationale: z.record(z.string(), z.unknown()),
    recurrence_count: z.int(),
    resolved_at: z.iso.date().nullable(),
    severity: z.string(),
    stage: z.string(),
    status: z.string(),
    updated_at: z.iso.datetime({ offset: true })
});

/**
 * ComplianceQueueResponse
 */
export const zComplianceQueueResponse = z.object({
    action_types: z.array(z.string()).optional(),
    limit: z.int(),
    offset: z.int(),
    recommendations: z.array(zRecommendationSummary),
    total: z.int()
});

/**
 * RecommendationEntityGroup
 *
 * One affiliate or player, with every open case the filter admits.
 *
 * The cases are the SAME rows `GET /v1/recommendations` returns, with the
 * same fields -- grouping adds a header, it does not add, hide, or rewrite
 * data. That is what keeps the audit list and the grouped inbox the same
 * truth seen twice. Since #1009 they are listed in HEADLINE order (severity,
 * outcome rank, recency, id), so the first case is the one the headline
 * names; the flat list keeps its own newest-first order.
 */
export const zRecommendationEntityGroup = z.object({
    case_count: z.int(),
    entity_ref: z.string(),
    entity_type: z.string(),
    headline: zRecommendationEntityHeadline,
    important: z.boolean(),
    latest_created_at: z.iso.datetime({ offset: true }),
    recommendations: z.array(zRecommendationSummary)
});

/**
 * HomeFeedSection
 *
 * One of the two feed sections, stated whole and served short.
 *
 * `entity_count` is the section's TRUE size, computed before the page is cut.
 * `entities` is at most `limit` of them. A client that reads the first and
 * renders the second can say "Still open - 7 entities" over three cards
 * without either number being a guess.
 */
export const zHomeFeedSection = z.object({
    entities: z.array(zRecommendationEntityGroup),
    entity_count: z.int()
});

/**
 * RecommendationWithholdReason
 *
 * Why recommendations are withheld -- and what the operator should do.
 *
 * These are not flavours of "unavailable". They ask different things of the
 * person reading the screen, which is why the API reports which one applies
 * instead of a bare boolean, and why the UI must not collapse them into one
 * banner (decision of 2026-08-01, founder + product):
 *
 * * `freeze` -- something is wrong; a human has to fix it. The set stays
 * withheld until they do.
 * * `incomplete_run` -- the last run stopped part-way; the set is being
 * rebuilt and another run resolves it. Nothing for the operator to do.
 * * `stalled_run` -- a run has been `running` far longer than one can take, so
 * the worker is gone rather than still working. Reported separately from
 * `incomplete_run` because that is a different fact, and the one an engineer
 * reads to tell a dead worker from a live one. Nothing for the operator to
 * do either: since #228 the loop has its own nightly CronJob and is a stage
 * of the five-minute upload-triggered dispatch, so a next run always comes
 * and supersedes the row (founder + product decision, 2026-08-07, #95). It
 * asked for a human until then, on a ground that was true when it was
 * written and false once the CronJobs were enabled -- that nothing would
 * ever supersede the row on its own.
 *
 * Which of the three asks the operator to act is decided in exactly one place,
 * and it is not this file: `withheldNeedsAttention` in
 * `app/frontend/src/lib/labels.ts`, pinned by `labels.test.ts`. A second copy
 * lived here until 2026-08-07 and is what kept the superseded split encoded
 * after the screen had stopped using it.
 *
 * A closed set the UI renders, so every value carries DE+EN labels and
 * `test_locale_parity` pins them (AGENTS.md 9.1).
 */
export const zRecommendationWithholdReason = z.enum([
    'freeze',
    'incomplete_run',
    'stalled_run'
]);

/**
 * DailyDigestResponse
 */
export const zDailyDigestResponse = z.object({
    affiliate_quality_movers: z.array(zAffiliateQualityMover),
    freeze_reason: z.string().nullable(),
    freeze_reason_code: z.string().nullable(),
    freeze_source: z.string().nullable(),
    frozen: z.boolean(),
    gated_accounting: zGatedAccounting,
    metric_date: z.iso.date().nullable(),
    open_critical_count: z.int(),
    pending_review_count: z.int(),
    recommendation_counts: z.record(z.string(), z.int()),
    rg_only_count: z.int(),
    withheld: z.boolean(),
    withheld_reason: zRecommendationWithholdReason.nullable()
});

/**
 * DashboardOverviewResponse
 *
 * The whole altitude-0 screen in one tenant-scoped read (#435).
 *
 * `operator_name` and `generated_at` are the print-to-PDF audit stamp's data
 * half; its sentence ("self-computed by BetQ from the raw event stream") is UI
 * copy and lives in `/locales`, never here -- a backend string rendered as page
 * copy is the section 9.1 failure this codebase has shipped four times.
 *
 * `last_completed_run_at` is item D's field, reused deliberately (the spec says
 * so): null means no nightly run has ever completed, which renders the waiting
 * glyph rather than a dashboard of zeros. `window` is null on the same tenant
 * for the neighbouring reason -- no computed day to anchor on.
 *
 * `withheld` qualifies the VERDICT COUNTS only, and this response is the one
 * place that distinction matters. The gate is about the recommendation set; the
 * daily metric rows behind the KPIs and the chart are unaffected by it, and
 * `daily_metrics` is not batched, so a failure there rolls back cleanly and
 * leaves the previous complete set intact. Blanking a month of NGR because
 * today's recommendation loop did not finish would be the "zero that means no
 * data" lie in the other direction.
 */
export const zDashboardOverviewResponse = z.object({
    affiliate_quality_movers: z.array(zAffiliateQualityMover),
    generated_at: z.iso.datetime({ offset: true }),
    kpis: z.array(zDashboardKpi),
    last_completed_run_at: z.iso.datetime({ offset: true }).nullable(),
    ngr_trend: z.array(zDashboardTrendPoint),
    open_verdicts: zDashboardOpenVerdicts,
    operator_name: z.string().nullable(),
    reporting_currency: z.string().nullable(),
    window: zDashboardWindow.nullable(),
    withheld: z.boolean(),
    withheld_reason: zRecommendationWithholdReason.nullable()
});

/**
 * HomeTodayResponse
 *
 * The Home / Today front door (#432, design item D).
 *
 * Two sections rather than one list, and the split is the server's: an entity
 * is `new_today` when at least one of its open cases was created by the newest
 * completed nightly run. The client must not infer that from timestamps --
 * with batch commits (#129) a run spans minutes, and "created after midnight"
 * is a different question with a different answer.
 *
 * `last_completed_run_at` carries both empty states in one field. Non-null is
 * the timestamp the `All clear` screen prints; null means no nightly run has
 * ever completed for this operator, which is the `First computation pending`
 * state -- and the reason it is a server truth rather than a guess is that
 * "green is earned, never decorative": a tenant that has never computed must
 * not be shown the check.
 *
 * `withheld`/`withheld_reason` are the same policy signal every other
 * recommendation read carries (#131). A front door that is empty because a run
 * did not complete must never read as a morning with nothing in it.
 *
 * `freeze_reason_code` is why that pair is not enough on its own (#102). Under
 * a freeze the three causes ask opposite things of the operator -- re-apply a
 * tenant_config, apply one at all, or go read a failed run -- and a screen
 * that cannot tell them apart prints the third for all three. The inbox and
 * the digest both key their sentence off this code and read it from
 * `GET /v1/digest/daily`; the front door is one request by design, so it
 * carries its own rather than fetching a whole digest for one string.
 *
 * `metric_date` is the second date here and deliberately not the same kind of
 * fact as the first (#758). `last_completed_run_at` is WHEN THE RUN FINISHED;
 * `metric_date` is WHICH DAY IT COMPUTED, and on a tenant fed historical data
 * the two are months apart. Home said "Needs you today" and named neither, so
 * the work date and the evidence date read as one thing.
 */
export const zHomeTodayResponse = z.object({
    freeze_reason_code: z.string().nullable(),
    last_completed_run_at: z.iso.datetime({ offset: true }).nullable(),
    limit: z.int(),
    metric_date: z.iso.date().nullable(),
    new_today: zHomeFeedSection,
    still_open: zHomeFeedSection,
    strip: zHomeStrip,
    withheld: z.boolean(),
    withheld_reason: zRecommendationWithholdReason.nullable()
});

/**
 * NotificationDispatchListResponse
 */
export const zNotificationDispatchListResponse = z.object({
    dispatches: z.array(zNotificationDispatchRow),
    limit: z.int(),
    offset: z.int(),
    withheld: z.boolean(),
    withheld_reason: zRecommendationWithholdReason.nullable()
});

/**
 * RecommendationEntityListResponse
 */
export const zRecommendationEntityListResponse = z.object({
    entities: z.array(zRecommendationEntityGroup),
    filter_entity_refs: z.array(z.string()),
    filter_patterns: z.array(z.string()),
    limit: z.int(),
    offset: z.int(),
    total: z.int(),
    withheld: z.boolean(),
    withheld_reason: zRecommendationWithholdReason.nullable()
});

/**
 * RecommendationListResponse
 */
export const zRecommendationListResponse = z.object({
    limit: z.int(),
    offset: z.int(),
    recommendations: z.array(zRecommendationSummary),
    total: z.int(),
    withheld: z.boolean(),
    withheld_reason: zRecommendationWithholdReason.nullable()
});

/**
 * SimulationAssertionRow
 */
export const zSimulationAssertionRow = z.object({
    actual: z.record(z.string(), z.unknown()),
    assertion_key: z.string(),
    expected: z.record(z.string(), z.unknown()),
    status: z.string()
});

/**
 * SimulationAssertionsResponse
 */
export const zSimulationAssertionsResponse = z.object({
    assertions: z.array(zSimulationAssertionRow),
    run_id: z.uuid()
});

/**
 * SimulationInjectRequest
 */
export const zSimulationInjectRequest = z.object({
    events: z.array(z.record(z.string(), z.unknown())).optional(),
    run_id: z.uuid(),
    scenario: z.string().min(1).max(128).optional().default('mvp_demo')
});

/**
 * SimulationInjectResponse
 */
export const zSimulationInjectResponse = z.object({
    accepted: z.int(),
    assertions: z.array(zSimulationAssertionRow),
    rejected: z.int(),
    run_id: z.uuid()
});

/**
 * SimulationRunResponse
 */
export const zSimulationRunResponse = z.object({
    created_at: z.iso.datetime({ offset: true }),
    finished_at: z.iso.datetime({ offset: true }).nullable(),
    run_id: z.uuid(),
    scenario: z.string(),
    status: z.string()
});

/**
 * SimulationStartRequest
 */
export const zSimulationStartRequest = z.object({
    parameters: z.record(z.string(), z.unknown()).optional(),
    scenario: z.string().min(1).max(128).optional().default('mvp_demo')
});

/**
 * SuppressedByCause
 *
 * Why each live suppressed case is being held, per the matrix in force.
 *
 * Classified against the ONE cell the gate asks --
 * `jurisdiction_action_matrix[meta.jurisdictions[0]][action_type]`, the same
 * cell `resolve_matrix_verdict` reads -- not against the jurisdiction stamped
 * on the row and not against the row's stored `matrix_verdict`. Both of those
 * are history: `regate_operator` re-derives from `jurisdictions[0]`, so the
 * question worth answering is whether the configuration in force still
 * justifies the hold.
 *
 * Exhaustive by construction: one if/elif/else over the rows the query
 * returned, with no membership test and no `continue`, so the three fields
 * always sum to `SuppressedAccounting.total`. That sum is what makes the
 * accounting checkable.
 *
 * * `authored_block` -- the cell says `blocked`. Legal intent working. Counted
 * so it is visible, never reported as a finding.
 * * `stale_gate` -- the cell says something other than `blocked`, yet the case
 * is still suppressed, so nothing in the configuration in force justifies
 * holding it. #169 closed the door that minted these in production: a case
 * with a standing compliance approval resolves to the authored cell, so the
 * stored-verdict mismatch that held it releases on the next re-gate. What
 * does NOT self-heal is a status-only drift over a matching stored gate --
 * an out-of-band write -- because the sweep deliberately compares the gate
 * triple, never the status (the status comparison was the disproven #169
 * fix). Such a row stays counted until a matrix change actually moves its
 * gate, which is exactly why this census exists.
 * * `undefined_cell` -- the matrix in force has no cell for this action at
 * all, so nothing can express a ruling on it. Over a config that validates
 * this means an `action_type` that is not a `MatrixActionType` member --
 * a row written before the enum was narrowed. It is also where a genuine
 * per-jurisdiction hole would land if `validate_cross_block_rules` ever
 * stopped requiring every action in every row.
 */
export const zSuppressedByCause = z.object({
    authored_block: z.int(),
    stale_gate: z.int(),
    undefined_cell: z.int()
});

/**
 * SuppressedAccounting
 *
 * The whole live suppressed population, and what explains it.
 *
 * Issue #162. A recommendation the matrix blocks becomes `suppressed`, which
 * is served on no list surface -- so without this it exists, is not terminal,
 * and is counted nowhere anyone can see. #161 pinned that the three inbox tab
 * counts deliberately do not sum to the live case count; this is where the
 * remainder is accounted for.
 *
 * `total` is an independent COUNT over
 * `(operator_id, status='suppressed', superseded_by IS NULL)` and needs no
 * tenant_config, so it is present on every path -- including the absent-config
 * and unparseable-config paths, where the whole population would otherwise go
 * uncounted. `by_cause` is null exactly when classification was impossible: no
 * stored tenant_config, or one that does not parse. Whenever it is non-null,
 * its three fields sum to `total`.
 */
export const zSuppressedAccounting = z.object({
    by_cause: zSuppressedByCause.nullish(),
    total: z.int()
});

/**
 * ConfigurationCheck
 */
export const zConfigurationCheck = z.object({
    check: zConfigurationCheckName,
    commission_chain: z.array(zCommissionChainPlatformFinding).nullish(),
    gated_accounting: zGatedAccounting.nullish(),
    reason: zConfigurationBlockReason.nullish(),
    state: zConfigurationCheckState,
    suppressed_accounting: zSuppressedAccounting.nullish()
});

/**
 * ConfigurationHealth
 *
 * Rollup plus the individual checks.
 *
 * `state` is the worst of the checks, with `blocked` ranked above `unknown`:
 * a condition we know is blocking is more actionable than one we cannot read.
 */
export const zConfigurationHealth = z.object({
    checks: z.array(zConfigurationCheck),
    state: zConfigurationCheckState
});

/**
 * IngestHealthResponse
 */
export const zIngestHealthResponse = z.object({
    attribution_divergence: zAttributionDivergence,
    configuration: zConfigurationHealth,
    freeze_reason_code: z.string().nullish(),
    frozen: z.boolean(),
    identity_flags: z.int(),
    identity_flags_state: zIdentityFlagState,
    sources: z.array(zIngestHealthSource),
    zone_integrity_ok: z.boolean().nullable()
});

/**
 * TaxModelType
 */
export const zTaxModelType = z.enum([
    'TURNOVER_TAX',
    'GGR_TAX',
    'NONE'
]);

/**
 * TenantCalibrationConstant
 *
 * One calibration constant of blocks 4 and 5, as the screen states it.
 *
 * `name` is the DOTTED tenant_config path and nothing else -- exactly the key
 * `constantGloss` looks up in the `calibration.*` locale namespace (#746), so
 * the screen renders a human sentence and never the path itself. Serving the
 * path rather than a pre-split namespace/leaf pair is deliberate: a lookup
 * against a parsed leaf can silently succeed on the wrong branch of a nested
 * namespace, which is the mistake the checklist gloss already avoids.
 *
 * `values` carries DECIMAL STRINGS, never JSON numbers. Five of these are
 * money (`net_loss_care_threshold`, `net_loss_care_relative_floor`,
 * `whale_threshold`, `cpa_amount`, `cpa_min_deposit_baseline`) and AGENTS.md
 * forbids a JSON float at a money boundary; serving the whole block one way
 * rather than two keeps a reader from having to know which of the 32 is
 * which. One entry for a scalar, two
 * for `retention.churn.reactivation_window_days`, which is a
 * `[start, end]` window rather than a single line.
 *
 * `flag` is separate from `values` because the one boolean in these two
 * blocks (`affiliate.deal_validation.global_nco_default`) is a closed set the
 * UI renders through `booleanLabel`. A stringified `"true"` in `values` would
 * reach a German screen as the English word (AGENTS.md section 9.1, failure
 * shape 1). Exactly one of the two is ever populated.
 */
export const zTenantCalibrationConstant = z.object({
    flag: z.boolean().nullish(),
    name: z.string(),
    values: z.array(z.string()).optional().default([])
});

/**
 * TenantCalibrationState
 *
 * The 32 calibration constants the two module blocks declare.
 *
 * Two lists rather than one, because the schema has two blocks and they
 * answer different questions: block 4 calibrates how a PLAYER is judged and
 * block 5 how an AFFILIATE is. The screen gives each its own heading, and an
 * operator looking for the churn boundary should not read past sixteen
 * affiliate thresholds to reach it.
 *
 * Declaration order is the schema's own, carried in the producer's spec table
 * rather than re-sorted here: the blocks group by sub-block
 * (`baseline`, `maturity_guard`, `churn`, `value`), and alphabetical order
 * would scatter the lifecycle ladder -- `hard_inactivity_days <
 * dormancy_days < churn_days` is a ladder the config validates as one, and
 * reading it out of order hides that it is one.
 */
export const zTenantCalibrationState = z.object({
    affiliate: z.array(zTenantCalibrationConstant),
    retention: z.array(zTenantCalibrationConstant)
});

/**
 * TenantConfigStatus
 *
 * Whether a scope has a readable tenant_config.
 *
 * `absent` and `invalid` are different operator problems and must not be
 * collapsed: `absent` means nothing was ever applied, `invalid` means a config
 * *is* applied but no longer validates. Telling an operator "no config is
 * applied" while a row sits in the database sends them looking for the wrong
 * thing (issue #17).
 */
export const zTenantConfigStatus = z.enum([
    'applied',
    'invalid',
    'absent'
]);

/**
 * CsvMappingListResponse
 */
export const zCsvMappingListResponse = z.object({
    claims_mappings: z.array(zClaimsMappingSummary).optional(),
    mappings: z.array(zCsvMappingSummary),
    operator_id: z.string(),
    tenant_config_revision: z.int().nullable(),
    tenant_config_status: zTenantConfigStatus
});

/**
 * TenantFinancialConstants
 *
 * The tenant's own financial constants, read-only (issue #633).
 *
 * Here for the same reason `reporting_currency` is: a definition surface has
 * to reflect the tenant's ACTUAL computation, "not one generic textbook
 * formula" (product review 2026-08-26, relayed on #633) -- and the constants
 * that make NGR and bonus cost tenant-specific live in
 * `tenant_config.financial`, which no API served. The definition the
 * altitude-0 tiles carry stays identical for every brand, by the module doc's
 * own rule; what this adds beside it is the numbers that definition is
 * evaluated with here.
 *
 * Three fields, not the block's five. `provider_fee_percent` and
 * `payment_fee_percent` are stored cost estimates that
 * `specs/tenant_config.schema.yaml` marks "NOT an NGR input"; rendering them
 * beside an NGR definition would state that they are one. The admin fee and
 * the tax deduction point are absent for a different reason: since #691 they
 * DO have a machine-readable home, but not a tenant-wide one -- they are
 * declared per affiliate platform on `adapters.claims_sources[]`
 * (`admin_fee_percent`, `tax_deducted_before_platform`), because the
 * commission chain is configured IN the platform. An operator on two
 * platforms has two of them, so neither is a constant this block could state
 * once; the admin fee is also withheld AFTER BetQ's NGR (founder,
 * 2026-09-02) and is not a term of the definition these tiles carry.
 *
 * Decimal STRINGS, as every numeric at this boundary already is
 * (`DashboardKpi.value`, AGENTS.md section 4). A rate that reaches the screen
 * through a JSON float is a rounding the operator never configured.
 *
 * Read-only, and no write path is added anywhere. Config-as-code in Git is
 * the administrative path at MVP (AGENTS.md section 5); this states what is
 * applied and cannot change it.
 */
export const zTenantFinancialConstants = z.object({
    bonus_conversion_rate_default: z.string(),
    tax_model_type: zTaxModelType,
    tax_rate_percent: z.string()
});

/**
 * AuthMeResponse
 */
export const zAuthMeResponse = z.object({
    actor_ref: z.string(),
    financial: zTenantFinancialConstants.nullish(),
    operator_id: z.string(),
    operator_name: z.string().nullish(),
    reporting_currency: z.string().nullish(),
    role: z.enum(['operator_user', 'operator_compliance'])
});

/**
 * AuthSessionResponse
 *
 * What login hands back, including both ends of the session's life (#268 S4).
 *
 * `expires_at` is the **idle** deadline and moves forward while the session is
 * used; `absolute_expires_at` is the cap and never moves. Both are on the
 * response because the frontend session handler re-mints the browser cookie
 * from this body and has to set its `Expires` to the cap -- a cookie cut at
 * the idle deadline is deleted by the browser before a renewal can reach it.
 */
export const zAuthSessionResponse = z.object({
    absolute_expires_at: z.iso.datetime({ offset: true }),
    expires_at: z.iso.datetime({ offset: true }),
    session_token: z.string(),
    user: zAuthMeResponse
});

/**
 * TenantModulesState
 *
 * Which of the three MVP modules the applied tenant_config enables.
 *
 * All three or nothing (see `TenantSettingsResponse`): a module rendered
 * "off" because its stored value could not be read is a claim about what
 * BetQ is doing for this operator, not a missing field.
 */
export const zTenantModulesState = z.object({
    affiliate_intelligence: z.boolean(),
    integration_orchestrator: z.boolean(),
    retention_intelligence: z.boolean()
});

/**
 * TenantNarrationState
 *
 * Whether AI narration is on for this tenant, and which backend writes it.
 *
 * Narration only ever puts prose around a decision the rules already made
 * (AGENTS.md section 4), so this says nothing about what BetQ recommends --
 * which is exactly why an operator has to be able to see it: a screen whose
 * sentences change with a config flag should say that the flag exists.
 *
 * `reasoning.hosted_model_ref` is not here. The contract's data list names
 * "narration on/off + backend" and stops there, and the model reference is a
 * per-tenant deployment detail rather than a fact about what the operator
 * reads.
 */
export const zTenantNarrationState = z.object({
    narration_backend: zReasoningBackend,
    narration_enabled: z.boolean()
});

/**
 * TenantSettingsResponse
 *
 * The tenant's effective configuration, read-only (issue #755).
 *
 * Every group is independently nullable and every group is all-or-nothing,
 * the posture `TenantFinancialConstants` already takes on `/v1/auth/me`: the
 * stored config is read as the raw dict it is, because a config written
 * before a validation rule existed must not stop this screen from answering,
 * and each group is then validated on its own and degraded to null when it
 * does not hold.
 *
 * All five null therefore means exactly one thing -- no readable tenant_config
 * is applied -- because a config that loads carries all five as required
 * fields. The screen states that rather than rendering an archetype nobody
 * configured.
 */
export const zTenantSettingsResponse = z.object({
    archetype: zOperatorArchetype.nullish(),
    calibration: zTenantCalibrationState.nullish(),
    jurisdictions: z.array(z.string()).nullish(),
    modules: zTenantModulesState.nullish(),
    narration: zTenantNarrationState.nullish()
});

/**
 * ValidationError
 */
export const zValidationError = z.object({
    ctx: z.record(z.string(), z.unknown()).optional(),
    input: z.unknown().optional(),
    loc: z.array(z.union([z.string(), z.int()])),
    msg: z.string(),
    type: z.string()
});

/**
 * HTTPValidationError
 */
export const zHttpValidationError = z.object({
    detail: z.array(zValidationError).optional()
});

/**
 * Response Health Health Get
 *
 * Successful Response
 */
export const zHealthHealthGetResponse = z.record(z.string(), z.string());

export const zListPlayerAttributionV1AffiliatesAttributionGetQuery = z.object({
    limit: z.int().gte(1).lte(200).optional().default(50),
    offset: z.int().gte(0).optional().default(0),
    attribution_month: z.iso.date().nullish(),
    affiliate_id: z.string().nullish()
});

/**
 * Successful Response
 */
export const zListPlayerAttributionV1AffiliatesAttributionGetResponse = zPlayerAttributionListResponse;

export const zUploadPlayerAttributionV1AffiliatesAttributionUploadPostBody = zBodyUploadPlayerAttributionV1AffiliatesAttributionUploadPost;

/**
 * Successful Response
 */
export const zUploadPlayerAttributionV1AffiliatesAttributionUploadPostResponse = zPlayerAttributionUploadResponse;

export const zListAffiliateClaimsV1AffiliatesClaimsGetQuery = z.object({
    limit: z.int().gte(1).lte(200).optional().default(50),
    offset: z.int().gte(0).optional().default(0),
    claim_month: z.iso.date().nullish()
});

/**
 * Successful Response
 */
export const zListAffiliateClaimsV1AffiliatesClaimsGetResponse = zAffiliateClaimsListResponse;

export const zUploadAffiliateClaimsV1AffiliatesClaimsUploadPostBody = zBodyUploadAffiliateClaimsV1AffiliatesClaimsUploadPost;

/**
 * Successful Response
 */
export const zUploadAffiliateClaimsV1AffiliatesClaimsUploadPostResponse = zAffiliateClaimUploadResponse;

export const zAffiliateQualityTableV1AffiliatesQualityGetQuery = z.object({
    limit: z.int().gte(1).lte(100).optional().default(25),
    offset: z.int().gte(0).optional().default(0),
    sort: zAffiliateQualitySort.optional().default('metric_date'),
    order: zAffiliateQualityOrder.optional().default('desc')
});

/**
 * Successful Response
 */
export const zAffiliateQualityTableV1AffiliatesQualityGetResponse = zAffiliateQualityTableResponse;

export const zAffiliateQualityV1AffiliatesAffiliateIdQualityGetPath = z.object({
    affiliate_id: z.string()
});

/**
 * Successful Response
 */
export const zAffiliateQualityV1AffiliatesAffiliateIdQualityGetResponse = zAffiliateQualityResponse;

export const zSimulateDealV1AffiliatesAffiliateIdSimulateDealPostBody = zDealSimulationRequest;

export const zSimulateDealV1AffiliatesAffiliateIdSimulateDealPostPath = z.object({
    affiliate_id: z.string()
});

/**
 * Successful Response
 */
export const zSimulateDealV1AffiliatesAffiliateIdSimulateDealPostResponse = zDealSimulationResponse;

/**
 * Successful Response
 */
export const zMeV1AuthMeGetResponse = zAuthMeResponse;

/**
 * Response Delete Session V1 Auth Session Delete
 *
 * Successful Response
 */
export const zDeleteSessionV1AuthSessionDeleteResponse = z.record(z.string(), z.string());

export const zCreateSessionV1AuthSessionPostBody = zAuthSessionCreateRequest;

/**
 * Response Create Session V1 Auth Session Post
 *
 * Successful Response
 */
export const zCreateSessionV1AuthSessionPostResponse = z.union([
    zAuthSessionResponse,
    zAuthTenantResolutionResponse
]);

/**
 * Successful Response
 */
export const zComplianceMatrixV1ComplianceMatrixGetResponse = zComplianceMatrixResponse;

export const zPatchComplianceMatrixV1ComplianceMatrixPatchBody = zComplianceMatrixPatchRequest;

/**
 * Successful Response
 */
export const zPatchComplianceMatrixV1ComplianceMatrixPatchResponse = zComplianceMatrixResponse;

export const zApproveComplianceMatrixV1ComplianceMatrixApprovalsPostBody = zComplianceMatrixApprovalRequest;

/**
 * Successful Response
 */
export const zApproveComplianceMatrixV1ComplianceMatrixApprovalsPostResponse = zComplianceMatrixResponse;

export const zPatchComplianceMatrixClassV1ComplianceMatrixClassPatchBody = zComplianceMatrixClassPatchRequest;

/**
 * Successful Response
 */
export const zPatchComplianceMatrixClassV1ComplianceMatrixClassPatchResponse = zComplianceMatrixResponse;

export const zComplianceMatrixHistoryV1ComplianceMatrixHistoryGetQuery = z.object({
    limit: z.int().gte(1).lte(100).optional().default(20)
});

/**
 * Successful Response
 */
export const zComplianceMatrixHistoryV1ComplianceMatrixHistoryGetResponse = zComplianceMatrixHistoryResponse;

export const zComplianceQueueV1ComplianceQueueGetQuery = z.object({
    limit: z.int().gte(1).lte(100).optional().default(50),
    offset: z.int().gte(0).optional().default(0),
    action_type: z.string().max(128).nullish()
});

/**
 * Successful Response
 */
export const zComplianceQueueV1ComplianceQueueGetResponse = zComplianceQueueResponse;

export const zBulkComplianceVerdictV1ComplianceQueueBulkVerdictPostBody = zBulkComplianceVerdictRequest;

/**
 * Successful Response
 */
export const zBulkComplianceVerdictV1ComplianceQueueBulkVerdictPostResponse = zBulkActionResponse;

export const zComplianceVerdictV1ComplianceQueueRecommendationIdVerdictPostBody = zComplianceVerdictRequest;

export const zComplianceVerdictV1ComplianceQueueRecommendationIdVerdictPostPath = z.object({
    recommendation_id: z.uuid()
});

/**
 * Successful Response
 */
export const zComplianceVerdictV1ComplianceQueueRecommendationIdVerdictPostResponse = zRecommendationDetail;

export const zComplianceReopenV1ComplianceRecommendationsRecommendationIdReopenPostBody = zComplianceReopenRequest;

export const zComplianceReopenV1ComplianceRecommendationsRecommendationIdReopenPostPath = z.object({
    recommendation_id: z.uuid()
});

/**
 * Successful Response
 */
export const zComplianceReopenV1ComplianceRecommendationsRecommendationIdReopenPostResponse = zRecommendationDetail;

export const zListComputationRequestsV1ComputationRequestsGetQuery = z.object({
    limit: z.int().gte(1).lte(100).optional().default(20),
    offset: z.int().gte(0).optional().default(0),
    status: zComputationRequestStatus.nullish()
});

/**
 * Successful Response
 */
export const zListComputationRequestsV1ComputationRequestsGetResponse = zComputationRequestListResponse;

export const zListComputationRunsV1ComputationRunsGetQuery = z.object({
    limit: z.int().gte(1).lte(100).optional().default(50),
    offset: z.int().gte(0).optional().default(0),
    status: zComputationRunStatus.nullish(),
    job_name: z.string().nullish()
});

/**
 * Successful Response
 */
export const zListComputationRunsV1ComputationRunsGetResponse = zComputationRunListResponse;

export const zGetComputationRunV1ComputationRunsRunIdGetPath = z.object({
    run_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetComputationRunV1ComputationRunsRunIdGetResponse = zComputationRunSummary;

/**
 * Successful Response
 */
export const zListCsvMappingsV1CsvMappingsGetResponse = zCsvMappingListResponse;

export const zPreviewCsvV1CsvPreviewPostBody = zBodyPreviewCsvV1CsvPreviewPost;

/**
 * Successful Response
 */
export const zPreviewCsvV1CsvPreviewPostResponse = zCsvPreviewResponse;

export const zUploadCsvV1CsvUploadPostBody = zBodyUploadCsvV1CsvUploadPost;

/**
 * Successful Response
 */
export const zUploadCsvV1CsvUploadPostResponse = zEventBatchResponse;

/**
 * Successful Response
 */
export const zDashboardOverviewEndpointV1DashboardOverviewGetResponse = zDashboardOverviewResponse;

export const zDailyDigestV1DigestDailyGetQuery = z.object({
    entity_type: z.string().nullish()
});

/**
 * Successful Response
 */
export const zDailyDigestV1DigestDailyGetResponse = zDailyDigestResponse;

export const zIngestEventsV1EventsPostBody = zRawEventBatchRequest;

/**
 * Successful Response
 */
export const zIngestEventsV1EventsPostResponse = zEventBatchResponse;

export const zListDailyAffiliateFeaturesV1FeaturesDailyAffiliatesGetQuery = z.object({
    limit: z.int().gte(1).lte(100).optional().default(50),
    offset: z.int().gte(0).optional().default(0),
    metric_date: z.iso.date().nullish()
});

/**
 * Successful Response
 */
export const zListDailyAffiliateFeaturesV1FeaturesDailyAffiliatesGetResponse = zDailyAffiliateFeaturesResponse;

export const zListDailyPlayerFeaturesV1FeaturesDailyPlayersGetQuery = z.object({
    limit: z.int().gte(1).lte(100).optional().default(50),
    offset: z.int().gte(0).optional().default(0),
    metric_date: z.iso.date().nullish()
});

/**
 * Successful Response
 */
export const zListDailyPlayerFeaturesV1FeaturesDailyPlayersGetResponse = zDailyPlayerFeaturesResponse;

export const zHomeTodayV1HomeTodayGetQuery = z.object({
    limit: z.int().gte(1).lte(50).optional().default(25),
    module: z.string().nullish()
});

/**
 * Successful Response
 */
export const zHomeTodayV1HomeTodayGetResponse = zHomeTodayResponse;

/**
 * Successful Response
 */
export const zListFreezesV1IngestFreezesGetResponse = zFreezeListResponse;

export const zResolveFreezesV1IngestFreezesResolvePostBody = zFreezeResolveRequest;

/**
 * Successful Response
 */
export const zResolveFreezesV1IngestFreezesResolvePostResponse = zFreezeResolveResponse;

/**
 * Successful Response
 */
export const zIngestHealthV1IngestHealthGetResponse = zIngestHealthResponse;

export const zListIngestJobsV1IngestJobsGetQuery = z.object({
    limit: z.int().gte(1).lte(100).optional().default(50),
    offset: z.int().gte(0).optional().default(0),
    status: z.string().nullish(),
    source_adapter: z.string().nullish()
});

/**
 * Successful Response
 */
export const zListIngestJobsV1IngestJobsGetResponse = zIngestJobListResponse;

export const zGetIngestJobV1IngestJobsJobIdGetPath = z.object({
    job_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetIngestJobV1IngestJobsJobIdGetResponse = zIngestJobSummary;

export const zListIngestFindingsV1IngestJobsJobIdFindingsGetPath = z.object({
    job_id: z.uuid()
});

export const zListIngestFindingsV1IngestJobsJobIdFindingsGetQuery = z.object({
    limit: z.int().gte(1).lte(100).optional().default(50),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zListIngestFindingsV1IngestJobsJobIdFindingsGetResponse = zIngestFindingListResponse;

export const zListIngestRejectionsV1IngestJobsJobIdRejectionsGetPath = z.object({
    job_id: z.uuid()
});

export const zListIngestRejectionsV1IngestJobsJobIdRejectionsGetQuery = z.object({
    limit: z.int().gte(1).lte(100).optional().default(50),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zListIngestRejectionsV1IngestJobsJobIdRejectionsGetResponse = zIngestRejectionListResponse;

/**
 * Successful Response
 */
export const zListIngestSourcesV1IngestSourcesGetResponse = zIngestSourcesResponse;

export const zListDailyAffiliateMetricsV1MetricsDailyAffiliatesGetQuery = z.object({
    limit: z.int().gte(1).lte(100).optional().default(50),
    offset: z.int().gte(0).optional().default(0),
    metric_date: z.iso.date().nullish()
});

/**
 * Successful Response
 */
export const zListDailyAffiliateMetricsV1MetricsDailyAffiliatesGetResponse = zDailyAffiliateMetricsResponse;

export const zListDailyFinancialMetricsV1MetricsDailyFinancialGetQuery = z.object({
    limit: z.int().gte(1).lte(100).optional().default(50),
    offset: z.int().gte(0).optional().default(0),
    metric_date: z.iso.date().nullish()
});

/**
 * Successful Response
 */
export const zListDailyFinancialMetricsV1MetricsDailyFinancialGetResponse = zDailyFinancialMetricsResponse;

export const zListDailyPlayerMetricsV1MetricsDailyPlayersGetQuery = z.object({
    limit: z.int().gte(1).lte(100).optional().default(50),
    offset: z.int().gte(0).optional().default(0),
    metric_date: z.iso.date().nullish()
});

/**
 * Successful Response
 */
export const zListDailyPlayerMetricsV1MetricsDailyPlayersGetResponse = zDailyPlayerMetricsResponse;

export const zNotificationDispatchesV1NotificationsDispatchesGetQuery = z.object({
    limit: z.int().gte(1).lte(100).optional().default(50),
    offset: z.int().gte(0).optional().default(0),
    status: zNotificationDispatchStatus.nullish(),
    order: zNotificationDispatchOrder.optional().default('newest_first')
});

/**
 * Successful Response
 */
export const zNotificationDispatchesV1NotificationsDispatchesGetResponse = zNotificationDispatchListResponse;

export const zUpdateNotificationDispatchV1NotificationsDispatchesDispatchIdPatchBody = zNotificationDispatchStatusRequest;

export const zUpdateNotificationDispatchV1NotificationsDispatchesDispatchIdPatchPath = z.object({
    dispatch_id: z.uuid()
});

/**
 * Successful Response
 */
export const zUpdateNotificationDispatchV1NotificationsDispatchesDispatchIdPatchResponse = zNotificationDispatchRow;

export const zListPlayerMarketingOptinsV1PlayersMarketingOptinsGetQuery = z.object({
    limit: z.int().gte(1).lte(200).optional().default(50),
    offset: z.int().gte(0).optional().default(0),
    optin_month: z.iso.date().nullish()
});

/**
 * Successful Response
 */
export const zListPlayerMarketingOptinsV1PlayersMarketingOptinsGetResponse = zPlayerOptInListResponse;

export const zUploadPlayerMarketingOptinsV1PlayersMarketingOptinsUploadPostBody = zBodyUploadPlayerMarketingOptinsV1PlayersMarketingOptinsUploadPost;

/**
 * Successful Response
 */
export const zUploadPlayerMarketingOptinsV1PlayersMarketingOptinsUploadPostResponse = zPlayerOptInUploadResponse;

export const zListRecommendationsV1RecommendationsGetQuery = z.object({
    limit: z.int().gte(1).lte(100).optional().default(50),
    offset: z.int().gte(0).optional().default(0),
    status: z.string().nullish(),
    tab: zInboxTab.nullish(),
    module: z.string().nullish(),
    severity: z.string().nullish(),
    entity_type: z.string().nullish(),
    entity: z.string().nullish(),
    from: z.iso.date().nullish(),
    to: z.iso.date().nullish()
});

/**
 * Successful Response
 */
export const zListRecommendationsV1RecommendationsGetResponse = zRecommendationListResponse;

export const zBulkRecommendationActionV1RecommendationsBulkPostBody = zBulkRecommendationActionRequest;

/**
 * Successful Response
 */
export const zBulkRecommendationActionV1RecommendationsBulkPostResponse = zBulkActionResponse;

export const zListRecommendationEntitiesV1RecommendationsEntitiesGetQuery = z.object({
    limit: z.int().gte(1).lte(50).optional().default(25),
    offset: z.int().gte(0).optional().default(0),
    tab: zInboxTab.nullish(),
    module: z.string().nullish(),
    severity: z.string().nullish(),
    entity_type: z.string().nullish(),
    entity: z.string().nullish(),
    pattern: z.string().nullish(),
    from: z.iso.date().nullish(),
    to: z.iso.date().nullish(),
    order: zEntityOrder.optional().default('latest')
});

/**
 * Successful Response
 */
export const zListRecommendationEntitiesV1RecommendationsEntitiesGetResponse = zRecommendationEntityListResponse;

export const zClearEntityImportantV1RecommendationsEntitiesEntityTypeEntityRefImportantDeletePath = z.object({
    entity_type: z.string().regex(/^(affiliate|player)$/),
    entity_ref: z.string().min(1).max(255)
});

/**
 * Successful Response
 */
export const zClearEntityImportantV1RecommendationsEntitiesEntityTypeEntityRefImportantDeleteResponse = zRecommendationEntityImportant;

export const zMarkEntityImportantV1RecommendationsEntitiesEntityTypeEntityRefImportantPutPath = z.object({
    entity_type: z.string().regex(/^(affiliate|player)$/),
    entity_ref: z.string().min(1).max(255)
});

/**
 * Successful Response
 */
export const zMarkEntityImportantV1RecommendationsEntitiesEntityTypeEntityRefImportantPutResponse = zRecommendationEntityImportant;

export const zGetRecommendationV1RecommendationsRecommendationIdGetPath = z.object({
    recommendation_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetRecommendationV1RecommendationsRecommendationIdGetResponse = zRecommendationDetail;

export const zAckRecommendationV1RecommendationsRecommendationIdAckPostPath = z.object({
    recommendation_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAckRecommendationV1RecommendationsRecommendationIdAckPostResponse = zRecommendationDetail;

export const zRecordCareActionV1RecommendationsRecommendationIdCareRecordPostBody = zCareRecordRequest;

export const zRecordCareActionV1RecommendationsRecommendationIdCareRecordPostPath = z.object({
    recommendation_id: z.uuid()
});

/**
 * Successful Response
 */
export const zRecordCareActionV1RecommendationsRecommendationIdCareRecordPostResponse = zRecommendationDetail;

export const zDismissRecommendationV1RecommendationsRecommendationIdDismissPostBody = zRecommendationActionRequest;

export const zDismissRecommendationV1RecommendationsRecommendationIdDismissPostPath = z.object({
    recommendation_id: z.uuid()
});

/**
 * Successful Response
 */
export const zDismissRecommendationV1RecommendationsRecommendationIdDismissPostResponse = zRecommendationDetail;

export const zExecuteRecommendationV1RecommendationsRecommendationIdExecutePostBody = zRecommendationActionRequest;

export const zExecuteRecommendationV1RecommendationsRecommendationIdExecutePostPath = z.object({
    recommendation_id: z.uuid()
});

/**
 * Successful Response
 */
export const zExecuteRecommendationV1RecommendationsRecommendationIdExecutePostResponse = zRecommendationDetail;

/**
 * Successful Response
 */
export const zTenantSettingsV1SettingsConfigurationGetResponse = zTenantSettingsResponse;

export const zInjectSimulationV1SimulationInjectPostBody = zSimulationInjectRequest;

/**
 * Successful Response
 */
export const zInjectSimulationV1SimulationInjectPostResponse = zSimulationInjectResponse;

export const zStartSimulationV1SimulationStartPostBody = zSimulationStartRequest;

/**
 * Successful Response
 */
export const zStartSimulationV1SimulationStartPostResponse = zSimulationRunResponse;

export const zSimulationAssertionsV1SimulationRunIdAssertionsGetPath = z.object({
    run_id: z.uuid()
});

/**
 * Successful Response
 */
export const zSimulationAssertionsV1SimulationRunIdAssertionsGetResponse = zSimulationAssertionsResponse;
